// Centralized user-facing strings. Each entry on `strings` (and `validationStrings`) is a getter that takes
// an optional `language` and returns the localized text; with no argument it resolves the current user's
// language via getLanguage(). The getters are intentionally self-describing (the key names the string), so
// they carry no per-entry JSDoc.
import { Meteor } from 'meteor/meteor';

/**
 * Returns the current user's language code (client only), defaulting to 'en-us'.
 * @returns the active language code
 */
export const getLanguage = () => {
  return Meteor.isClient && Meteor.user()?.language || 'en-us';
};

export const strings = {
  Username(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Username';
    }
  },
  Password(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Password';
    }
  },
  NewPassword(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New Password';
    }
  },
  SignIn(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sign In';
    }
  },
  SignUp(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sign up';
    }
  },
  Welcome(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Welcome';
    }
  },
  WelcomeBack(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Welcome Back!';
    }
  },
  PleaseEnterYourLoginDetailsBelow(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please enter your login details below.';
    }
  },
  Submit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Submit';
    }
  },
  CreateFreeAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Create a free account';
    }
  },
  DontHaveAnAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Don\'t have an account?';
    }
  },
  MakeOneHere(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Make one here';
    }
  },
  InvalidUsernameOrPassword(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invalid Username or Password';
    }
  },
  Logout(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Logout';
    }
  },
  ForgotPassword(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Forgot password?';
    }
  },
  SendResetLink(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Send Reset Link';
    }
  },
  ForgotPasswordInstructions(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please enter your email below';
    }
  },
  ForgotPasswordEmailInstructions(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'If you requested to reset your password, please click the link below.';
    }
  },
  ResetPassword(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Reset Password';
    }
  },
  SetPassword(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Set Password';
    }
  },
  PleaseCheckYourEmailAndOrSmsForPasswordResetLink(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please check your email and/or mobile device for a password reset link.';
    }
  },
  PasswordSuccessfullyChanged(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Password successfully changed';
    }
  },
  YourUsernameIs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Your username is';
    }
  },
  Profile(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Profile';
    }
  },
  MyProfile(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'My Profile';
    }
  },
  ProfilePicture(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Profile Picture';
    }
  },
  FirstName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'First Name';
    }
  },
  LastName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Last Name';
    }
  },
  Save(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Save';
    }
  },
  ClearAutomationProfile(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Clear Automation Profile';
    }
  },
  ClearAutomationProfileTooltip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Clears the browser profile for this account. If MFA is enabled, then a full re-connection will be required';
    }
  },
  AutomationProfileCleared(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Automation profile cleared — the next run will start fresh';
    }
  },
  Update(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Update';
    }
  },
  Updated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Updated';
    }
  },
  Saved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Saved';
    }
  },
  Organizations(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Organizations';
    }
  },
  Organization(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Organization';
    }
  },
  EditOrganization(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Edit Organization';
    }
  },
  Name(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Name';
    }
  },
  Address(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Address';
    }
  },
  Addresses(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Addresses';
    }
  },
  City(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'City';
    }
  },
  State(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'State';
    }
  },
  Country(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Country';
    }
  },
  ZipCode(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Zip Code';
    }
  },
  Zipcode(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Zip Code';
    }
  },
  New(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New';
    }
  },
  Edit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Edit';
    }
  },
  Search(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Search';
    }
  },
  Users(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Users';
    }
  },
  User(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'User';
    }
  },
  EditUser(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Edit User';
    }
  },
  SaveUser(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Save User';
    }
  },
  Email(language?: string) {
    language = language || getLanguage();
    switch (language) {
      case 'fr-ca':
        return 'courriel';
      default: return 'Email';
    }
  },
  UtilityIntakeEmailTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Your email is being requested so we can contact you in case any issues are encountered during the integration process. Your email will not be shared with any third parties.';
    }
  },
  InvalidPasswordError(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Error: password must contain at least 1 number, 1 letter and a special character (!&_?*)';
    }
  },
  UsernameAlreadyTaken(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Error: email already exists';
    }
  },
  InvalidEmailAddress(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Error: invalid email address';
    }
  },
  ThankYouForSigningUpMessage(email: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Thank you for joining Luminia.io. Please check your inbox at ${email} to verify your email address.`;
    }
  },
  GoToLogin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Go to Login';
    }
  },
  Phone(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Phone';
    }
  },
  NoResults(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No Results';
    }
  },
  Roles(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Roles';
    }
  },
  SelectAll(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Select All';
    }
  },
  ChooseOrganizationFirst(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Choose Organization First';
    }
  },
  Expand(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Expand';
    }
  },
  Collapse(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Collapse';
    }
  },
  ThereAreNoRecordsForThisPage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'There are no records for this page';
    }
  },
  CreateOne(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Create One';
    }
  },
  StartTime(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Start Time';
    }
  },
  EndTime(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'End Time';
    }
  },
  StartDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Start Date';
    }
  },
  EndDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'End Date';
    }
  },
  Language(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Language';
    }
  },
  English(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'English';
    }
  },
  French(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'French';
    }
  },
  ThisIsAnAutomatedEmailPleaseDoNotReply(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This is an automated email, please do not reply.';
    }
  },
  Hi(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Hi';
    }
  },
  Timezone(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Timezone';
    }
  },
  DateTimeFormat(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Date/Time Format';
    }
  },
  Sunday(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sunday';
    }
  },
  Monday(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Monday';
    }
  },
  Tuesday(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tuesday';
    }
  },
  Wednesday(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Wednesday';
    }
  },
  Thursday(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Thursday';
    }
  },
  Friday(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Friday';
    }
  },
  Saturday(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Saturday';
    }
  },
  January(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'January';
    }
  },
  February(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'February';
    }
  },
  March(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'March';
    }
  },
  April(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'April';
    }
  },
  May(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'May';
    }
  },
  June(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'June';
    }
  },
  July(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'July';
    }
  },
  August(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'August';
    }
  },
  September(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'September';
    }
  },
  October(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'October';
    }
  },
  November(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'November';
    }
  },
  December(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'December';
    }
  },
  Status(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Status';
    }
  },
  Delete(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Delete';
    }
  },
  Deleted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Deleted';
    }
  },
  Image(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Image';
    }
  },
  Images(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Images';
    }
  },
  Resend(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Resend';
    }
  },
  NotPermitted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Not permitted';
    }
  },
  NotFound(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Not Found';
    }
  },
  DeleteSelected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Delete Selected';
    }
  },
  ActivateSelected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Activate Selected';
    }
  },
  DeactivateSelected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Deactivate Selected';
    }
  },
  Deactivated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Deactivated';
    }
  },
  Filters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Filters';
    }
  },
  Time(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Time';
    }
  },
  Remove(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Remove';
    }
  },
  Cancel(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Cancel';
    }
  },
  Action(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Action';
    }
  },
  Yes(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Yes';
    }
  },
  No(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No';
    }
  },
  Active(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Active';
    }
  },
  Date(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Date';
    }
  },
  Sent(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sent';
    }
  },
  Deactivate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Deactivate';
    }
  },
  Total(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total';
    }
  },
  Inactive(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Inactive';
    }
  },
  All(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'All';
    }
  },
  AnErrorOccurredPleaseTryAgainOrContactSupport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'An error occurred. Please try again or contact support.';
    }
  },
  BackToLogin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Back to Login';
    }
  },
  DayOfWeek(day: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: switch (day) {
        case 0:
          return 'Sunday';
        case 1:
          return 'Monday';
        case 2:
          return 'Tuesday';
        case 3:
          return 'Wednesday';
        case 4:
          return 'Thursday';
        case 5:
          return 'Friday';
        case 6:
          return 'Saturday';
      }
    }
  },
  DayOfWeekShort(day: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: switch (day) {
        case 0:
          return 'Sun';
        case 1:
          return 'Mon';
        case 2:
          return 'Tue';
        case 3:
          return 'Wed';
        case 4:
          return 'Thu';
        case 5:
          return 'Fri';
        case 6:
          return 'Sat';
        default:
          return '';
      }
    }
  },
  MonthOfYear(month: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: switch (month) {
        case 0:
          return 'January';
        case 1:
          return 'February';
        case 2:
          return 'March';
        case 3:
          return 'April';
        case 4:
          return 'May';
        case 5:
          return 'June';
        case 6:
          return 'July';
        case 7:
          return 'August';
        case 8:
          return 'September';
        case 9:
          return 'October';
        case 10:
          return 'November';
        case 11:
          return 'December';
        default:
          return '';
      }
    }
  },
  MonthOfYearShort(month: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: switch (month) {
        case 0:
          return 'Jan';
        case 1:
          return 'Feb';
        case 2:
          return 'Mar';
        case 3:
          return 'Apr';
        case 4:
          return 'May';
        case 5:
          return 'Jun';
        case 6:
          return 'Jul';
        case 7:
          return 'Aug';
        case 8:
          return 'Sep';
        case 9:
          return 'Oct';
        case 10:
          return 'Nov';
        case 11:
          return 'Dec';
        default:
          return '';
      }
    }
  },
  Settings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Settings';
    }
  },
  SettingsQuoteRequestRecipients(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Custom Quote Request Recipients';
    }
  },
  SettingsPmInvoiceNotifyEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PM Invoice Notification Email (AP inbox for invoice uploads)';
    }
  },
  SettingsPmInvoiceApprovedNotifyEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PM Invoice Approved Notification Email (notified when an invoice is approved)';
    }
  },
  SettingsPmDocumentNotifyEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PM Document Notification Email (daily digest of task documents uploaded and approved)';
    }
  },
  SettingsPmDocumentApprovedNotifyEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PM Document Approved Notification Email (notified when a task document is approved)';
    }
  },
  SettingsPlaywrightHeaded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Run Playwright Headed (watch via VNC)';
    }
  },
  SettingsPlaywrightDisplayServer(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Playwright Display Server (X-forward fallback, e.g. 192.168.1.50:0; empty = shared :99 / VNC)';
    }
  },
  Done(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Done';
    }
  },
  Open(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Open';
    }
  },
  Completion(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Completion';
    }
  },
  AttentionNow(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Attention Now';
    }
  },
  Capacity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Capacity';
    }
  },
  Solar(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar';
    }
  },
  Bess(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'BESS';
    }
  },
  LuminiaDevFee(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Luminia Dev Fee';
    }
  },
  DevFeeForDraw(draw: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Dev fee – ${draw}`;
    }
  },
  DevFeesTotal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Dev fees total';
    }
  },
  TenantBilling(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tenant Billing';
    }
  },
  TotalFees(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total fees';
    }
  },
  ShowMoreDetail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Show more detail';
    }
  },
  LuminiaId(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Luminia ID';
    }
  },
  AncillaryFees(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Ancillary Fees';
    }
  },
  BuyoutSavings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Buyout Savings';
    }
  },
  DrawnByChangeOrders(drawn: string, left: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${drawn} drawn by change orders · ${left} left`;
    }
  },
  BeforeChangeOrders(gross: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${gross} before change orders`;
    }
  },
  RolesAndAccess(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Roles & Access';
    }
  },
  RolesAndAccessSubtitle(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Role capabilities and account assignments';
    }
  },
  RoleCapabilities(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Role Capabilities';
    }
  },
  AccountAssignments(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Account Assignments';
    }
  },
  Feature(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Feature';
    }
  },
  AccessRule(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Access rule';
    }
  },
  RoleSuperUser(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Super User';
    }
  },
  RoleRicExecutive(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'RIC Executive';
    }
  },
  RoleProjectManagement(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Mgmt';
    }
  },
  RoleProductionMeters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Production Meters';
    }
  },
  RoleProspector(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Prospector';
    }
  },
  RoleConsumptionMeters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Consumption Meters';
    }
  },
  RoleCalpine(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Calpine';
    }
  },
  AllUsers(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'All users';
    }
  },
  RolePmDocumentApprover(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PM Document Approver';
    }
  },
  RoleLuminia(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Luminia';
    }
  },
  PrepaidPpa(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Prepaid PPA'; } },
  CategoryReview(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Technical Review'; } },
  DealerPayments(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Dealer Payments'; } },
  Prepayment(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Prepayment'; } },
  DealerBuildCost(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Dealer Build Cost'; } },
  TrueUp(language?: string) { language = language || getLanguage(); switch (language) { default: return 'True-up'; } },
  AsBuilt(language?: string) { language = language || getLanguage(); switch (language) { default: return 'As-built'; } },
  Proposed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Proposed'; } },
  NetToLuminia(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Net'; } },
  NotSetYet(language?: string) { language = language || getLanguage(); switch (language) { default: return 'not set'; } },
  TrueUpHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'The final share scales by as-built \u00f7 proposed system size'; } },
  ReviewReadyForReview(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Ready for Review'; } },
  ReviewApproved(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Approved'; } },
  ReviewDeficiency(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Deficiency'; } },
  MilestoneReview(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Milestone Review'; } },
  RaiseDeficiency(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Raise Deficiency'; } },
  BackToReview(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Back to Review'; } },
  ReviewNote(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Note'; } },
  DeficiencyNeedsNote(language?: string) { language = language || getLanguage(); switch (language) { default: return 'A deficiency needs a note saying what has to be fixed'; } },
  DeficiencyNotePlaceholder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'What has to be fixed before this can be approved'; } },
  AwaitingReview(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Awaiting Review'; } },
  OpenDeficiencies(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Open Deficiencies'; } },
  AverageApprovalTime(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Avg. Approval Time'; } },
  ReviewedBy(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Reviewed by'; } },
  MilestoneReviewHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This milestone waits for a reviewer: approval is what completes it'; } },
  Days(language?: string) { language = language || getLanguage(); switch (language) { default: return 'days'; } },
  DocumentApproval(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document Approval';
    }
  },
  RoleAccounting(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Accounting';
    }
  },
  InvoiceApproval(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invoice approval';
    }
  },
  SyncQuickbooksInvoices(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sync QuickBooks Invoices';
    }
  },
  QuickbooksSourcesUpdated(updated: number, unchanged: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${updated} source${updated === 1 ? '' : 's'} updated from QuickBooks, ${unchanged} unchanged`;
    }
  },
  NotYetApproved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'no';
    }
  },
  DateApproved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Date approved';
    }
  },
  ApprovedBy(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Approved by';
    }
  },
  Unapproved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Un-approved';
    }
  },
  Unapprove(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Un-approve';
    }
  },
  UnapproveInvoiceConfirm(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Take this approval back? The invoice stays as it is; only the approval is cleared, and the change is recorded in History.';
    }
  },
  AwaitingApproval(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Awaiting approval';
    }
  },
  NoMatchingInvoices(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No invoices match these filters.';
    }
  },
  NoUnpaidInvoices(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No unpaid invoices';
    }
  },
  Invite(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invite';
    }
  },
  InviteToOrganization(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invite to your organization';
    }
  },
  InviteRolesHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Roles the invitee will hold in your organization. You can only grant roles you hold yourself.';
    }
  },
  InvitationSent(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invitation sent';
    }
  },
  InvitationToLuminia(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invitation to Luminia.io';
    }
  },
  InvitationEmailIntro(orgName: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `You have been invited to join ${orgName} on Luminia.io. Click the button below to accept the invitation and set up your account.`;
    }
  },
  InvitationEmailRoles(roles: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `You will be granted the following roles: ${roles}.`;
    }
  },
  InvitationEmailExpiry(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This invitation link expires in 7 days. If you were not expecting it, you can safely ignore this email.';
    }
  },
  AcceptInvitation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Accept Invitation';
    }
  },
  YouHaveBeenInvitedToJoin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return "You've been invited to join";
    }
  },
  SigningUpAs(email: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Signing up as ${email}`;
    }
  },
  TellUsYourNameToFinish(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tell us your name to finish setting up your account.';
    }
  },
  InvalidOrExpiredInvitation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This invitation link is invalid or has expired. Please ask for a new invitation.';
    }
  },
  PhoneNumberOptional(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Phone Number (optional)';
    }
  },
  JoinOrganization(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Join';
    }
  },
  SupportAdmin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Support Admin';
    }
  },
  RuleAllSignedInUsers(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'All signed-in users';
    }
  },
  RuleSuperUserOnly(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Super User only';
    }
  },
  RolesMatrixHelp(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Super User unlocks everything, so it is checked on every row. Orange checks mark roles that must be held together (an "and" gate); blue checks mark interchangeable roles, any one of which satisfies the row alongside the orange ones; a single green check grants the feature on its own.';
    }
  },
  LegendGrantsAlone(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Grants the feature on its own';
    }
  },
  LegendAndGate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Several roles required together (and gate)';
    }
  },
  SuperUserNotAccountRole(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Super User is a user-level flag, not an assignable account role.';
    }
  },
  NoAccounts(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No accounts';
    }
  },
  BudgetAndInvoiceApproval(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Budget & invoice approval';
    }
  },
  ProjectsAndTasks(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Projects & tasks';
    }
  },
  AccountAndPreferences(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Account & preferences';
    }
  },
  ApplicationSettings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Application settings';
    }
  },
  Commissioning(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Commissioning';
    }
  },
  EstimatedPto(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Estimated PTO';
    }
  },
  TaskCompletion(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Task Completion';
    }
  },
  OnSchedule(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'on schedule';
    }
  },
  DaysLate(days: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${days} ${days === 1 ? 'day' : 'days'} late`;
    }
  },
  NotScheduled(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'not scheduled';
    }
  },
  StoreNumber(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Store #';
    }
  },
  ProjectAge(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Age';
    }
  },
  DaysCount(days: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${days} ${days === 1 ? 'day' : 'days'}`;
    }
  },
  DaysOverdue(days: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${days} ${days === 1 ? 'day' : 'days'} overdue`;
    }
  },
  DueInDays(days: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return days === 0 ? 'due today' : `due in ${days} ${days === 1 ? 'day' : 'days'}`;
    }
  },
  PlannedOn(date: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `planned ${date}`;
    }
  },
  NothingNeedsAttention(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Nothing needs attention right now';
    }
  },
  OverdueCount(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${count} overdue`;
    }
  },
  ItemsCount(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${count} ${count === 1 ? 'item' : 'items'}`;
    }
  },
  ConnectQuickbooks(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Connect QuickBooks';
    }
  },
  Reconnect(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Reconnect';
    }
  },
  Disconnect(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Disconnect';
    }
  },
  NotConnected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Not connected';
    }
  },
  QuickbooksConnectionExpired(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Connection expired — reconnect to restore access';
    }
  },
  QuickbooksAccessRevoked(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Access was revoked in QuickBooks — reconnect to restore access';
    }
  },
  QuickbooksTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Read-only. Luminia never writes back to QuickBooks.';
    }
  },
  QuickbooksConnected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Connected. You can close this window — nothing else is needed.';
    }
  },
  QuickbooksDenied(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The connection was declined. Nothing was changed.';
    }
  },
  QuickbooksFailed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The connection could not be completed. Please try again, or ask for a new link.';
    }
  },
  QuickbooksLinkExpired(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This link has already been used or has expired. Ask for a new one.';
    }
  },
  CopyLink(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Copy Link';
    }
  },
  QuickbooksShareTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Valid 7 days, single use. Anyone with the link can attach a QuickBooks company to this org.';
    }
  },
  QuickbooksCashSync(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'QuickBooks Cash Sync';
    }
  },
  QuickbooksCashSyncHelp(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pulls invoice and payment figures onto the Budget tab as <b>actuals</b>: each Source\'s actual is the invoice total less its open balance (what has been received so far), and its paid date is set once the invoice is settled (an open balance under $1 counts as settled). Inflows only — see the Outflow sync for bills. Runs hourly; this is for checking it. <b>Dry run</b> reports what it would write without writing.';
    }
  },
  DryRun(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Dry Run';
    }
  },
  NoChanges(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No changes';
    }
  },
  RefreshToken(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Refresh Token';
    }
  },
  QuickbooksTokenRefreshed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Token refreshed. Access expires';
    }
  },
  QuickbooksRefreshTokenExpires(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Refresh token expires';
    }
  },
  QuickbooksLastRefreshed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Last refreshed';
    }
  },
  QuickbooksNeverRefreshed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'never refreshed';
    }
  },
  QuickbooksApiExplorer(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'QuickBooks API Explorer';
    }
  },
  QuickbooksApiExplorerHelp(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Send a read request to the Intuit Accounting API and read the raw response. Picking an endpoint fills in the path and parameters; both stay editable. <b>GET only</b> — nothing here can write to QuickBooks.';
    }
  },
  QuickbooksApiTokenHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Leave blank to use the stored connection, which supplies its own company and environment. A pasted token is remembered in this browser and never reaches the Luminia database — clear the field to forget it.';
    }
  },
  QuickbooksApiCompanyHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Intuit realmId. Required alongside a pasted token.';
    }
  },
  QuickbooksApiParamsHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Query string, unencoded — for the query endpoint that is query=select * from Bill.';
    }
  },
  Endpoint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Endpoint';
    }
  },
  Path(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Path';
    }
  },
  AccessToken(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Access Token';
    }
  },
  MinorVersion(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Minor Version';
    }
  },
  CompanyId(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Company Id';
    }
  },
  Response(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Response';
    }
  },
  Sandbox(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sandbox';
    }
  },
  Fix(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Fix';
    }
  },
  PasswordRules(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Password must be at least 8 characters';
    }
  },
  SetUsernameOrPassword(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Set Username/Password';
    }
  },
  Admin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Admin';
    }
  },
  Accounts(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Accounts';
    }
  },
  Account(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Account';
    }
  },
  SuperUser(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Super User';
    }
  },
  YouAreNotPermitted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'You do not have access to this page.';
    }
  },
  Theme(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Theme';
    }
  },
  Light(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Light';
    }
  },
  PleaseSelectAnOrganization(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please select an Organization';
    }
  },
  PleaseZoomInFurtherToEnableTheApnLayer(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please zoom in further to enable the APN layer';
    }
  },
  PleaseZoomInFurtherToEnableThisLayer(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please zoom in further to enable this layer';
    }
  },
  NoGoogleFootprintForThisBuilding(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No Google footprint found for this building';
    }
  },
  AccountIsDeactivated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Account is deactivated';
    }
  },
  GoToHomePage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Go to homepage.';
    }
  },
  Print(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Print';
    }
  },
  GeneratingPrintView(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generating Print/PDF...';
    }
  },
  Row(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Row';
    }
  },
  UnknownVertical(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Unknown vertical';
    }
  },
  ProjectsToCreate(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${count} new ${count === 1 ? 'project' : 'projects'}`;
    }
  },
  ProjectsCreated(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${count} ${count === 1 ? 'project' : 'projects'} created`;
    }
  },
  CannotCreateProjectsHere(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rows without a Project ID were skipped: you do not have permission to create projects';
    }
  },
  Logs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Logs';
    }
  },
  LogEntries(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${count} ${count === 1 ? 'entry' : 'entries'}`;
    }
  },
  NewLogEntries(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${count} new ${count === 1 ? 'entry' : 'entries'}`;
    }
  },
  NoLogEntries(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No log entries match these filters';
    }
  },
  SearchMessages(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Search messages';
    }
  },
  Live(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Live';
    }
  },
  Paused(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Paused';
    }
  },
  Pause(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pause';
    }
  },
  Resume(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Resume';
    }
  },
  ShowMore(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Show more';
    }
  },
  Data(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Data';
    }
  },
  Trace(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Trace';
    }
  },
  Info(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Info';
    }
  },
  Warning(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Warning';
    }
  },
  Error(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Error';
    }
  },
  Critical(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Critical';
    }
  },
  File(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'File';
    }
  },
  Logo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Logo';
    }
  },
  OwnerLogo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Owner Logo';
    }
  },
  EpcLogo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC Logo';
    }
  },
  UtilityCompanyLogo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility Company Logo';
    }
  },
  ProjectLogo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Logo';
    }
  },
  SharedAcrossProjects(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Shared — changing this affects every project using it';
    }
  },
  NewAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New Account';
    }
  },
  From(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'From';
    }
  },
  To(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'To';
    }
  },
  Result(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Result';
    }
  },
  Results(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Results';
    }
  },
  Title(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Title';
    }
  },
  Description(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Description';
    }
  },
  Verify(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Verify';
    }
  },
  ThankYou(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Thank You';
    }
  },
  For(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'For';
    }
  },
  iAgreeToTheTermsOfService(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'I agree to the Terms of Service';
    }
  },
  TermsAcknowledgement(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'You acknowledge that by clicking "Sign In" you are agreeing to Luminia\'s';
    }
  },
  TermsOfServiceLink(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Terms of Service';
    }
  },
  PrivacyPolicy(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Privacy Policy';
    }
  },
  And(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'and';
    }
  },
  TermsOfService(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'terms of service';
    }
  },
  AlreadyHaveAnAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Already have an account?';
    }
  },
  WeJustNeededToMakeSureYourEmailAddressIsReal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'We just needed to make sure that your email address is real. Please click the button below to verify.';
    }
  },
  EmailVerification(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Email Verification';
    }
  },
  ThankYouYourEmailHasBeenVerified(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Thank you! Your email has been verified.';
    }
  },
  InvalidOrExpiredVerificationToken(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invalid or expired verification token.';
    }
  },
  PleaseVerifyYourEmailAddressBeforeLoggingIn(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please verify your email address before login in.';
    }
  },
  PleaseVerifyYourEmailAddressToAccessThisPage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please verify your email address to access this page.';
    }
  },
  MyOrganization(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'My Organization';
    }
  },
  VerificationRequiredMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please verify your email address to unlock all features. Click here to resend the verification email.';
    }
  },
  SelectFile(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Select File';
    }
  },
  Uploading(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Uploading';
    }
  },
  Upload(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Upload';
    }
  },
  Manage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Manage';
    }
  },
  Selected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Selected';
    }
  },
  Home(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Home';
    }
  },
  ResendVerificationEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Resend Verification Email';
    }
  },
  Complete(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Complete';
    }
  },
  Camera(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Camera';
    }
  },
  SendLoginLink(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Send Login Link';
    }
  },
  Conversations(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Conversations';
    }
  },
  Conversation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Conversation';
    }
  },
  Messages(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Messages';
    }
  },
  Message(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Message';
    }
  },
  You(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'You';
    }
  },
  NewMessages(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New messages';
    }
  },
  Optional(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Optional';
    }
  },
  Back(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Back';
    }
  },
  Environment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Environment';
    }
  },
  SupportTicket(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Support Ticket';
    }
  },
  SupportTickets(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Support Tickets';
    }
  },
  Support(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Support';
    }
  },
  CaseNumber(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Case Number';
    }
  },
  Subject(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Subject';
    }
  },
  UpdatedAt(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Updated';
    }
  },
  CreatedAt(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Created';
    }
  },
  SupportCase(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Support Case';
    }
  },
  Files(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Files';
    }
  },
  Reply(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Reply';
    }
  },
  YourSupportTicketHasBeenUpdated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Your support ticket has been updated. Please do not reply to this email, but View Details below to reply in the app.';
    }
  },
  ContactInformation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Contact Information';
    }
  },
  OtherSettings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Other Settings';
    }
  },
  YouHaveNewMessages(num: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `You have ${num} new message${num === 1 ? '' : 's'}`;
    }
  },
  NewMessageFrom(sender: string, about: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `New message from ${sender} — ${about}`;
    }
  },
  MessageSenderLuminia(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Luminia';
    }
  },
  MessageSenderExternal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'External';
    }
  },
  OpenProject(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Open project';
    }
  },
  NoConversationsYet(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No conversations yet';
    }
  },
  NewConversation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New conversation';
    }
  },
  StartConversation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Start conversation';
    }
  },
  ConversationNameOptional(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Name (optional)';
    }
  },
  AddPeople(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add people';
    }
  },
  MessageDeleted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Message deleted';
    }
  },
  SeenBy(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Seen by';
    }
  },
  AttachFile(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Attach a file';
    }
  },
  Yesterday(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Yesterday';
    }
  },
  MessageComposerHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enter to send · Shift+Enter for a new line · @ to mention';
    }
  },
  MentionedYou(sender: string, about: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${sender} mentioned you — ${about}`;
    }
  },
  UnreadOnly(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Unread only';
    }
  },
  AllVerticals(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'All verticals';
    }
  },
  EditWindowClosed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This message can no longer be changed';
    }
  },
  PmDocumentDigestAwaiting(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Awaiting approval';
    }
  },
  PmDocumentDigestSummary(uploaded: number, approved: number, awaiting: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${uploaded} uploaded · ${approved} approved · ${awaiting} awaiting approval`;
    }
  },
  PmDocumentDigestWaitingSince(since: string, days: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `waiting since ${since} (${days} ${days === 1 ? 'day' : 'days'})`;
    }
  },
  RejectedDocumentWithReason(label: string, reason: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Rejected "${label}": ${reason}`;
    }
  },
  GoToMessageCentre(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Go To Message Centre';
    }
  },
  Impersonate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Impersonate';
    }
  },
  ImpersonationViewingAs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Viewing as';
    }
  },
  ImpersonationExit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Exit impersonation';
    }
  },
  ImpersonationExitFailed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Could not return to your own account. Please log in again.';
    }
  },
  AmountMustBeANumber(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Amount must be a number';
    }
  },
  ChartResolution(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Resolution';
    }
  },
  ChartResolutionFifteenMin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return '15-minute';
    }
  },
  ChartResolutionHourly(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'hourly';
    }
  },
  ChartResolutionDaily(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'daily';
    }
  },
  ChartResolutionMonthly(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'monthly';
    }
  },
  Integrations(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Integrations';
    }
  },
  VerifyEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Verify Email';
    }
  },
  EmailVerificationEmailSent(email: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Please check your ${email} inbox for a verification link.`;
    }
  },
  NameEmailPassword(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Name, Email, Password';
    }
  },
  ThirdPartyConnections(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Third Party Connections';
    }
  },
  DeleteMyAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Delete My Account';
    }
  },
  AreYouSureYouWantToDeleteYourAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Are you sure you want to delete your profile? This action cannot be undone!';
    }
  },
  ClickTheLinkBelowToLoginToYourAccount(username: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Click below to login to your account as ${username}`;
    }
  },
  LinkSentPleaseCheckYourInbox(email: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Link Sent. Please check your inbox at ${email}`;
    }
  },
  Link(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Link';
    }
  },
  UseGoogle(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Use Google';
    }
  },
  UseDiscord(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Use Discord';
    }
  },
  ThankYouForUsingAuth(serviceName: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Thank you for using ${serviceName} auth. You may close this tab.`;
    }
  },
  AlmostDoneClickButtonBelowToCompleteAuth(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Almost Done! Click the button below to complete your authorization';
    }
  },
  CompleteAuthorization(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Complete Authorization';
    }
  },
  Ok(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Ok';
    }
  },
  DevTools(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Dev Tools';
    }
  },
  Address2(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Address 2';
    }
  },
  Suite(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Suite';
    }
  },
  ViewDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'View Details';
    }
  },
  Project(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project';
    }
  },
  Projects(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Projects';
    }
  },
  SolarOnly(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Only';
    }
  },
  SolarPlusUpgrades(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar+Upgrades';
    }
  },
  WhatKindOfProposalIsTheCustomerLookingFor(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'What kind of proposal is the customer looking for?';
    }
  },
  WhichUpgradesWouldTheCustomerLike(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Which upgrade(s) would the customer like?';
    }
  },
  EnergyStorageSystem(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy Storage System';
    }
  },
  Roof(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Roof';
    }
  },
  EvChargers(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EV Chargers';
    }
  },
  HvacChiller(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'HVAC / Chiller';
    }
  },
  EnergyManagement(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy Management';
    }
  },
  Lighting(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lighting';
    }
  },
  WaterConservationIrrigationSystems(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Water Conservation / Irrigation Systems';
    }
  },
  Other(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Other';
    }
  },
  DoYouHaveAnEstimateOfSystemParameters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Do you have an estimate of system parameters?';
    }
  },
  Line(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Line';
    }
  },
  Line2(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Line 2';
    }
  },
  SystemSize(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'System Size';
    }
  },
  AnnualProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Annual Production';
    }
  },
  AnnualConsumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Annual Usage';
    }
  },
  EpcPrice(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC Price';
    }
  },
  AvoidedCost(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Avoided Cost';
    }
  },
  InstallType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Install Type';
    }
  },
  BuildingType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Building Type';
    }
  },
  Example(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Example';
    }
  },
  Rooftop(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rooftop';
    }
  },
  GroundMount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Ground Mount';
    }
  },
  Carport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Carport';
    }
  },
  Multiple(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Multiple';
    }
  },
  NewProject(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New Project';
    }
  },
  ProjectType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'P';
    }
  },
  County(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'County';
    }
  },
  UtilityProvider(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility Provider';
    }
  },
  ProposalType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Proposal Type';
    }
  },
  ProjectInfo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Info';
    }
  },
  SolarSystem(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar System';
    }
  },
  EssCapacity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Capacity';
    }
  },
  EssSize(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Size';
    }
  },
  EssEpcPrice(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Install Price';
    }
  },
  DocPendingApprovalHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This document is pending approval by Luminia';
    }
  },
  AddNote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add a note';
    }
  },
  SubmitNoteHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Ctrl + Enter';
    }
  },
  NoNotesYet(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No notes yet';
    }
  },
  Edited(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'edited';
    }
  },
  Notes(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Notes';
    }
  },
  ProjectSystemImageMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This system image will be used in the generation of proposals. The recommended upload size is 512 x 512 pixels or larger.';
    }
  },
  PrimaryPricingInputs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Primary Pricing Inputs';
    }
  },
  AdditionalPricingInputs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Additional Pricing Inputs';
    }
  },
  EnrichmentDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enrichment Details';
    }
  },
  InvestmentTaxCreditAdder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Investment Tax Credit Adder';
    }
  },
  SolarPpaEscalator(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar PPA Escalator';
    }
  },
  PpaTermLengthYears(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PPA Term Length (years)';
    }
  },
  BuyoutYear1(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Buyout Year, 1 (year)';
    }
  },
  BuyoutYear2(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Buyout Year, 2 (year)';
    }
  },
  Incentives(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Incentives';
    }
  },
  SolarSystemIncentive(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar System Incentive';
    }
  },
  EnergyStorageIncentive(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy Storage Incentive';
    }
  },
  OtherIncentive(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Other Incentive';
    }
  },
  IncentiveTerm(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Incentive Term';
    }
  },
  AnnualIncentiveEscalation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Annual Incentive Escalation';
    }
  },
  IncentiveRate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Incentive Rate';
    }
  },
  ProjectSystemSizeTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total Estimated System Size (rooftop, ground mount, carport, multiple)';
    }
  },
  ProjectEstimatedOneYearProductionTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total Estimated Year 1 Production based on System Size (kW)';
    }
  },
  ProjectEpcPriceTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total EPC price ($/W) for solar, including equipment, installation, permitting, commissioning, and ancillary costs; excluding incentives.';
    }
  },
  ProjectAvoidedCostTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Avoided Cost $ per kilowatt hour (example 0.14)';
    }
  },
  ProjectSystemTypeTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Select the type of solar installation';
    }
  },
  ProjectBuildingTypeTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Select the building type';
    }
  },
  ProjectEssCapacityTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total ESS Capacity (kWh) desired';
    }
  },
  ProjectEssSizeTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total ESS Power / Size (kW) – default is 2-hour discharge';
    }
  },
  ProjectEssEpcPriceTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Install Price ($/kWh) for installation, permitting, commissioning, and ancillary costs; excluding incentives.';
    }
  },
  ProjectEssAvoidedCostTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Avoided Cost $ per kilowatt hour (example 150)';
    }
  },
  ProjectInvestmentTaxCreditAdderTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Investment Tax Credit adder, above standard 30%';
    }
  },
  UpFrontGrantRebate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Up-front grant / rebate';
    }
  },
  UpFrontGrantRebateTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Renewable energy incentive that is paid out at or prior to the system achieving commercial operation.';
    }
  },
  PerformanceBasedIncentiveRec(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Performance-based incentive / REC';
    }
  },
  PerformanceBasedIncentiveRecTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Performance-based incentives (PBI) and Renewable Energy Credits (REC) are incentives that are paid based on the actual energy production of the solar system. Typically, these are paid based on an energy ($/kWh) basis over a period of time.';
    }
  },
  ProjectIncentiveTermTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Duration of the performance-based incentive or renewable energy credit contract term (e.g., 10 years). A value between 0 and 25 years may be entered.';
    }
  },
  ProjectAnnualIncentiveEscalationTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Amount that the performance-based incentive or renewable energy credit is expected to increase or decrease year-over-year. A value between -0.1 (-10%) and 0.1 (10%) may be entered.';
    }
  },
  ProjectIncentiveRateTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Initial (year 1) contracted rate in $/MWh of the performance-based incentive or renewable energy credit. A value between $0/MWh and $300/MWh may be entered.';
    }
  },
  ProjectAnnualProductionTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total Estimated Year 1 Production based on System Size (kW)';
    }
  },
  ProjectAnnualConsumptionTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total Estimated Annual Usage kWh';
    }
  },
  EssCapacitySystemSizeInvalidMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Capacity value too large for Solar PV to charge consistently. Please lower ESS Capacity and re-run';
    }
  },
  EpcPriceInvalidMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC Price must be between $1.00/W and $5.00/W. If EPC Price is outside of this range, please contact us using the Request option below';
    }
  },
  AvoidedCostPriceInvalidMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Avoided Cost must be between $0.07/kWh and $0.50/kWh. If Avoided Cost is outside this range, please contact us using the Request option below';
    }
  },
  SpecificEnergyInvalidMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Estimated Year 1 Production (kWh)/System Size (kW) must be between 1100 kWh/kW and 2200 kWh/kW. If the value is outside this range, please contact us using the Request option below';
    }
  },
  EssCapacityInvalidMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Capacity must be at least 120 kWh. If ESS Capacity is below this value, please contact us using the Request option below';
    }
  },
  EssSizeInvalidMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Luminia\'s minimum ESS Size to price is 60 kW. Luminia\'s maximum ESS Size in kW to price must be equal to or less than the ESS Capacity in kWh';
    }
  },
  EssEpcPriceInvalidMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Install Price must be at least $200/kWh. If ESS Install Price is below this value, please contact us using the Request option below';
    }
  },
  CePpaQuote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CE PPA Quote';
    }
  },
  CreditPpaQuote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Credit PPA Quote';
    }
  },
  SolarPlusEss(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar + ESS';
    }
  },
  EpcPriceExcludesIncentives(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC price ($/W) excludes incentives';
    }
  },
  DoesThePropertyOwnerHaveInvestmentGradeCredit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Does the property owner have investment grade credit?';
    }
  },
  IDontKnow(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'I don\'t know';
    }
  },
  Quotes(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Quotes';
    }
  },
  RemoveFromTask(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Remove from this task';
    }
  },
  RemoveFromTaskConfirm(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Remove this detail from the task? The detail and its values are kept — it just stops being required here.';
    }
  },
  Quote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Quote';
    }
  },
  QuoteType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Quote Type';
    }
  },
  ProductionMeters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Production Meters';
    }
  },
  ProductionMeter(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Production Meter';
    }
  },
  Identifier(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Identifier';
    }
  },
  Type(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Type';
    }
  },
  Domain(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Domain';
    }
  },
  CommissioningDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Commissioning Date';
    }
  },
  PpaRate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PPA Rate';
    }
  },
  PpaEscalator(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PPA Escalator';
    }
  },
  FixedPayment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Fixed Payment';
    }
  },
  DegradationRate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Degradation Rate';
    }
  },
  ForecastedProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Forecasted Production';
    }
  },
  PullProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pull Production';
    }
  },
  Tags(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tags';
    }
  },
  Production(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Production';
    }
  },
  SolarProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Production';
    }
  },
  ViewProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'View Production';
    }
  },
  Go(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Go';
    }
  },
  Running(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Running';
    }
  },
  SolarBills(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Bills';
    }
  },
  SolarBill(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Bill';
    }
  },
  AccountNumber(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Account Number';
    }
  },
  DueDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Due Date';
    }
  },
  IssuedDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Issued Date';
    }
  },
  TotalCharges(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total Charges';
    }
  },
  Ledger(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Ledger';
    }
  },
  BillingAccountNumber(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Billing Account Number';
    }
  },
  SolarPlusEnergyStorage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar + Energy Storage';
    }
  },
  EnergyStorageOnly(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy Storage Only';
    }
  },
  PropertyDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Property Details';
    }
  },
  SolarInputs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Inputs';
    }
  },
  EnergyStorageInputs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy Storage Inputs';
    }
  },
  EssAvoidedCost(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Avoided Cost';
    }
  },
  SolarAvoidedCost(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Avoided Cost';
    }
  },
  PvCostPerWdc(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PV Cost ($/W-DC)';
    }
  },
  EssCostPerKwh(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Cost ($/kWh)';
    }
  },
  WinningStrategy(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Winning Strategy';
    }
  },
  EssStrategyStacked(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Stacking (DCM & EA)';
    }
  },
  EssStrategyEa(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy Arbitrage (EA)';
    }
  },
  Inputs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Inputs';
    }
  },
  EnergyToolbase(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy Toolbase';
    }
  },
  LoadProfileParameters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Load Profile Parameters';
    }
  },
  SimulationStartDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Simulation Start Date';
    }
  },
  DataSource(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Data Source';
    }
  },
  Sector(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sector';
    }
  },
  ResolvedBuildingType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Resolved Building Type';
    }
  },
  Gisjoin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'County (GISJOIN)';
    }
  },
  ClimateZone(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Climate Zone';
    }
  },
  ComstockRelease(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ComStock Release';
    }
  },
  Randomized(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Randomized';
    }
  },
  Fallbacks(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Fallbacks';
    }
  },
  Milestones(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Milestones';
    }
  },
  Actual(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Actual';
    }
  },
  Finances(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Finances';
    }
  },
  Board(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Board';
    }
  },
  Table(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Table';
    }
  },
  Map(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Map';
    }
  },
  Geocoding(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'geocoding';
    }
  },
  ComingSoon(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Coming soon';
    }
  },
  Owner(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Owner';
    }
  },
  Epc(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC';
    }
  },
  Epcs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPCs';
    }
  },
  Owners(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Owners';
    }
  },
  OwnersSubtitle(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Property owners / site hosts and their logos';
    }
  },
  EpcsSubtitle(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC contractors and their logos';
    }
  },
  UtilitiesSubtitle(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility companies and their logos';
    }
  },
  CreateNamed(name: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Create "${name}"`;
    }
  },
  Unassigned(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Unassigned';
    }
  },
  InProgress(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'In Progress';
    }
  },
  Installed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Installed';
    }
  },
  OnHold(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'On Hold';
    }
  },
  Canceled(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Canceled';
    }
  },
  Deferred(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Deferred';
    }
  },
  ProjectStatus(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Status';
    }
  },
  ChangeStatus(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Change Status';
    }
  },
  StatusReason(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Reason';
    }
  },
  StatusReasonPlaceholder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Why is the project on hold or canceled?';
    }
  },
  StatusReasonRequired(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'A reason is required to put a project on hold or cancel it.';
    }
  },
  StatusUpdated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Status updated';
    }
  },
  NextMilestone(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Next Milestone';
    }
  },
  NextTask(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Next Task';
    }
  },
  FundingSources(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Funding Sources';
    }
  },
  AddFundingSource(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add Funding Source';
    }
  },
  CashSource(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Source';
    }
  },
  CashUse(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Use';
    }
  },
  Invoiced(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invoiced';
    }
  },
  Paid(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Paid';
    }
  },
  NetMargin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Net Margin';
    }
  },
  Estimated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Estimated';
    }
  },
  Variance(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Variance';
    }
  },
  Metric(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Metric';
    }
  },
  Parameters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Parameters';
    }
  },
  Unit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Unit';
    }
  },
  Category(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Category';
    }
  },
  CategorySiteDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Site Details';
    }
  },
  CategoryProjectDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Details';
    }
  },
  CategoryAgreements(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Agreement Details';
    }
  },
  CategorySiteReview(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Site Review & EPC';
    }
  },
  CategoryPermitting(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Permitting & Interconnection';
    }
  },
  CategoryEquipment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Equipment';
    }
  },
  CategoryWorkOrders(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Work Orders';
    }
  },
  CategoryLinks(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Links';
    }
  },
  CategoryModeled(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Modeled';
    }
  },
  CategoryEpcPayments(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC Milestone Payments';
    }
  },
  CategoryConstruction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Construction';
    }
  },
  ParameterTypeText(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Text';
    }
  },
  ParameterTypeInteger(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Integer';
    }
  },
  ParameterTypeNumber(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Number';
    }
  },
  ParameterTypeBoolean(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Yes / No';
    }
  },
  ParameterTypeDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Date';
    }
  },
  ParameterTypeDateTime(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Date & Time';
    }
  },
  Completed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Completed';
    }
  },
  Reopened(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Reopened';
    }
  },
  Replanned(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Replanned';
    }
  },
  Reverted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Reverted';
    }
  },
  VersionAdded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New version';
    }
  },
  OwnerChanged(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Owner changed';
    }
  },
  CashEvents(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Cash events';
    }
  },
  Hidden(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Hidden';
    }
  },
  ShowAll(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Show all';
    }
  },
  Hide(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Hide';
    }
  },
  HideTheseEvents(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Hide events of this type';
    }
  },
  Links(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Links';
    }
  },
  ProjectDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Details';
    }
  },
  Budget(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Budget';
    }
  },
  AllRoles(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'All roles';
    }
  },
  NotifyAssigned(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'You were assigned';
    }
  },
  NotifyDocNeedsApproval(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'A document needs approval';
    }
  },
  NotifyDocApproved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document approved';
    }
  },
  NotifyDocRejected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document rejected';
    }
  },
  PmDocumentDigestSubject(vertical: string, day: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `[${vertical}] PM document digest — ${day}`;
    }
  },
  ProjectManagementSettings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Management Settings';
    }
  },
  PmSettingsHint(org: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Notification inboxes for ${org}, per vertical. Several addresses can be separated by commas; a blank field sends nothing.`;
    }
  },
  PmSettingsSelectOrg(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Select an organization to edit its Project Management settings.';
    }
  },
  InvalidEmailList(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enter one or more email addresses, separated by commas.';
    }
  },
  SendDigest(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Send Digest';
    }
  },
  SendDigestTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Emails today\'s digest to the saved address now, without waiting for the 7 AM run.';
    }
  },
  PmDigestSent(to: string, day: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Digest for ${day} sent to ${to}`;
    }
  },
  PmDigestNothingToSend(day: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Nothing to send for ${day}: no uploads, approvals or documents awaiting approval in this vertical`;
    }
  },
  CreateProjects(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Create a project (incl. tracker imports)';
    }
  },
  OptionalRequirements(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Optional requirements (excuse a task parameter or document)';
    }
  },
  WorkOrderSplit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Work order split';
    }
  },
  WorkOrderSplitHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'How the Total Solar Cost is drawn across the three work orders. The estimates follow these shares whenever the Total Solar Cost changes; invoiced amounts still come from QuickBooks.';
    }
  },
  ShareOfTotalSolarCost(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Share of Total Solar Cost';
    }
  },
  SharesTotal(total: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Total: ${total}%`;
    }
  },
  DrawSharesMustTotal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The three shares must total 100%.';
    }
  },
  DrawSharesInvalid(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Each share must be a percentage between 0 and 100.';
    }
  },
  DrawMissing(draw: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `This project has no "${draw}" work-order draw to set a share on.`;
    }
  },
  DevFeeSplit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Dev fee split';
    }
  },
  DevFeeSplitHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'How the dev fee is shared across the three work orders. Each share is released once its work order has money in.';
    }
  },
  ShareOfDevFee(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Share of dev fee';
    }
  },
  DevFeeSplitSaved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Dev fee split saved';
    }
  },
  WorkOrderSplitSaved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Work order split saved';
    }
  },
  DealerSplit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Payment Split';
    }
  },
  DealerSplitHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The share of the System Purchase Price each milestone releases. The partner’s agreement sets the default; change it here for this project only.';
    }
  },
  DealerSplitSaved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Payment split saved';
    }
  },
  DealerSharesMustTotal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The shares must total 100%';
    }
  },
  ShareOfPurchasePrice(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Share of Purchase Price';
    }
  },
  SystemPurchasePrice(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'System Purchase Price';
    }
  },
  CustomerPrepayment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Customer Prepayment';
    }
  },
  CustomerPrepaymentHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Collected by the dealer directly and credited against the Fund’s payments. Not cash into this project.';
    }
  },
  Gross(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Gross';
    }
  },
  Collected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Collected';
    }
  },
  RoleInvoiceApprover(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invoice Approver';
    }
  },
  LegendOrGate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Either of these roles, with the required ones (or gate)';
    }
  },
  NothingToExport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Nothing to export';
    }
  },
  Exporting(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Exporting…';
    }
  },
  TasksCount(n: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${n} task${n === 1 ? '' : 's'}`;
    }
  },
  PmApproverDigestSubject(day: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Your PM document digest — ${day}`;
    }
  },
  PmApproverDigestIntro(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The documents of the tasks assigned to you.';
    }
  },
  PmDocumentDigestUploaded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Uploaded';
    }
  },
  PmDocumentDigestApproved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Approved';
    }
  },
  NotifyChangeOrderAccepted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Change order accepted';
    }
  },
  NotifyChangeOrderRejected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Change order rejected';
    }
  },
  NotifyStakeholderAdded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'You were added to a project';
    }
  },
  NotifyInvoiceUploaded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New invoice uploaded';
    }
  },
  NotifyInvoiceApproved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invoice approved';
    }
  },
  NotificationSettings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project management notifications';
    }
  },
  InApp(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'In-app';
    }
  },
  NotifTypeAssignment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Milestone / task assignments';
    }
  },
  NotifTypeDocumentApproval(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document approval requests (email as a daily digest)';
    }
  },
  NotifTypeDocumentDecision(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document approved / rejected';
    }
  },
  NotifTypeChangeOrderDecision(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Change order decisions';
    }
  },
  NotifTypeStakeholderAdded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Added to a project';
    }
  },
  NotifTypeInvoiceUploaded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invoice uploaded';
    }
  },
  NotifTypeInvoiceApproved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Invoice approved';
    }
  },
  NotifTypeMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Messages';
    }
  },
  Url(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'URL';
    }
  },
  History(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'History';
    }
  },
  NoHistory(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No changes recorded';
    }
  },
  Close(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Close';
    }
  },
  FinancialModel(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Financial Model';
    }
  },
  UploadFinancialModel(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Upload Financial Model';
    }
  },
  FinancialModelUploaded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Financial model uploaded and approved';
    }
  },
  ReviewModelValues(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Review model values';
    }
  },
  ModelValue(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Model value';
    }
  },
  CurrentEstimate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Current estimate';
    }
  },
  PromoteToActual(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Promote to Actual';
    }
  },
  NothingParsedFromModel(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Nothing could be read from the model';
    }
  },
  NoAssignedDocuments(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No documents — you only see documents delivered by tasks assigned to you.';
    }
  },
  NoFinancialModelUploaded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No financial model uploaded';
    }
  },
  ModelFileNotFound(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Model file not found';
    }
  },
  ConsumptionPullProblems(accounts: number, files: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Pull finished with problems: ${accounts} account(s) could not be read${files ? `, ${files} downloaded file(s) could not be processed` : ''}. See the logs.`;
    }
  },
  ConsumptionPullComplete(intervalsFiles: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return intervalsFiles ? `Pull complete: ${intervalsFiles} file(s) processed` : 'Pull complete';
    }
  },
  AlsoEnergyAccountAlreadyConnected(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'That AlsoEnergy account is already connected — one account can only be connected once, in one organisation';
    }
  },
  UnrecognizedPricingModel(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Not a pricing model this vertical can read';
    }
  },
  ModelRecalculated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The workbook was saved without calculated values, so its formulas were calculated with LibreOffice before reading.';
    }
  },
  ModelNotRecalculated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The workbook was saved without calculated values and could not be calculated, so its formula figures are missing. Open it in Excel, save it, and upload it again.';
    }
  },
  CouldNotConvertModel(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Could not convert the .ods model';
    }
  },
  CouldNotParseModelOutput(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Could not parse model output';
    }
  },
  UseRemainder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Use remainder'; } },
  RaisedFromInvoice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Raised from invoice'; } },
  ChangeOrder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Change Order'; } },
  NewChangeOrderFromInvoiceHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'An amount here raises a NEW change order for this invoice, submitted for review. It is not an existing payment.'; } },
  ChangeOrders(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Change Orders';
    }
  },
  AddChangeOrder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add Change Order';
    }
  },
  Direction(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Direction'; } },
  Rationale(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rationale';
    }
  },
  Submitted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Submitted';
    }
  },
  UnderReview(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Under Review';
    }
  },
  Accepted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Accepted';
    }
  },
  Accept(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Accept';
    }
  },
  Review(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Review';
    }
  },
  Contingency(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Contingency';
    }
  },
  Contacts(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Contacts';
    }
  },
  NewPortfolio(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'New Portfolio';
    }
  },
  AddNewPortfolio(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add new portfolio';
    }
  },
  Portfolios(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Portfolios';
    }
  },
  DeletePortfolioAndProjects(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Delete portfolio and projects';
    }
  },
  DeletePortfolioOnly(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Delete portfolio only';
    }
  },
  PortfolioDeleteExplainer(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return '"Delete portfolio only" keeps the projects and removes them from the portfolio.';
    }
  },
  OnsiteContact(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Onsite Contact';
    }
  },
  OnsiteContactName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Onsite Contact Name';
    }
  },
  OnsiteEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Onsite Email';
    }
  },
  OnsitePhone(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Onsite Phone';
    }
  },
  ClientId(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Client ID';
    }
  },
  ClientSecret(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Client Secret';
    }
  },
  UpdateCredentials(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Update Credentials';
    }
  },
  PaymentTrigger(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Payment Trigger';
    }
  },
  PaymentTriggerHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Payment trigger — reaching this milestone releases a milestone payment';
    }
  },
  RequiresIcApprovalHint(pct: number, threshold: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Over ${pct}% of the contract base (${threshold}), so the Investment Committee should approve it. Nothing here blocks accepting it.`;
    }
  },
  Attachments(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Attachments';
    }
  },
  AddAttachment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add Attachment';
    }
  },
  TracksActual(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tracks an actual';
    }
  },
  NotesStatus(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Notes/Status'; } },
  StatusScheduled(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Scheduled'; } },
  StatusSubmitted(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Submitted'; } },
  StatusApproved(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Approved'; } },
  StatusCompleted(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Completed'; } },
  StatusExecuted(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Executed'; } },
  Participants(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Participants';
    }
  },
  Value(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Value';
    }
  },
  PurchaseOrders(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Purchase Orders';
    }
  },
  Sku(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'SKU';
    }
  },
  Quantity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Quantity';
    }
  },
  QuantityOnHand(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'On Hand';
    }
  },
  Available(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Available';
    }
  },
  Assigned(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Assigned';
    }
  },
  Ownership(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Ownership';
    }
  },
  SafeHarbor(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Safe Harbor';
    }
  },
  LuminiaOwned(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Luminia-Owned';
    }
  },
  HeldForFinancier(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Held for Financier';
    }
  },
  Equipment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Equipment';
    }
  },
  Assignments(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Assignments';
    }
  },
  AssignToProject(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Assign to Project';
    }
  },
  AddItem(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add Item';
    }
  },
  InventoryItem(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Inventory Item';
    }
  },
  DataRoom(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Data Room';
    }
  },
  BatchUpload(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Batch Upload';
    }
  },
  ApprovedVersionsOnly(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Approved versions only';
    }
  },
  DocumentTypes(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document Types';
    }
  },
  Skipped(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Skipped';
    }
  },
  NoDocumentsMatched(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No documents matched the selection';
    }
  },
  AddPurchaseOrder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add Purchase Order';
    }
  },
  OrderDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Order Date';
    }
  },
  EquipmentType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Equipment Type';
    }
  },
  Serials(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Serial Numbers';
    }
  },
  Inventory(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Inventory';
    }
  },
  Supplier(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Supplier';
    }
  },
  Reference(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Reference';
    }
  },
  Source(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Source';
    }
  },
  AddPayment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add Payment';
    }
  },
  Party(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Party';
    }
  },
  Debt(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Payment';
    }
  },
  TaxEquity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tax Equity';
    }
  },
  PrePaidLoan(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pre-Paid Loan';
    }
  },
  CapitalAdvance(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Capital Advance';
    }
  },
  Stakeholders(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Stakeholders';
    }
  },
  Stakeholder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Stakeholder';
    }
  },
  Vendor(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Vendor'; } },
  Payee(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Payee';
    }
  },
  Internal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Internal';
    }
  },
  External(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'External';
    }
  },
  Portfolio(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Portfolio';
    }
  },
  TotalProjects(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total Projects';
    }
  },
  RateTariff(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rate Tariff';
    }
  },
  RateSwitchCca(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rate Switch (CCA)';
    }
  },
  CcaTariff(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CCA Tariff';
    }
  },
  RackingType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Racking Type';
    }
  },
  IntervalDataMode(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Interval Data Mode';
    }
  },
  Lead(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lead';
    }
  },
  MarketingProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Indicative Proposal';
    }
  },
  OwnershipType(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Ownership Type';
    }
  },
  PropertyOwnerOwnerUserPartner(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Property Owner / Owner-User / Partner';
    }
  },
  EmployeeRepresentativeOfOwner(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Employee / Representative of Owner';
    }
  },
  NonOwnerTenant(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Non-Owner Tenant';
    }
  },
  Dataroom(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Dataroom';
    }
  },
  Agreements(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Agreements';
    }
  },
  PrimeRates(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Prime Rates';
    }
  },
  PrimeRate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Prime Rate';
    }
  },
  Rate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rate';
    }
  },
  LastBillDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Last Bill Date';
    }
  },
  PropertyOwner(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Property Owner';
    }
  },
  Enrich(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enrich';
    }
  },
  EnrichIncomplete(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enrich Incomplete';
    }
  },
  QueuedForEnrichment(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Queued ${count} prospect${count === 1 ? '' : 's'} for enrichment`;
    }
  },
  NoIncompleteProspects(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No incomplete prospects to enrich';
    }
  },
  ContactName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Contact Name';
    }
  },
  ContactPhone(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Contact Phone';
    }
  },
  ContactEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Contact Email';
    }
  },
  ContactAddress(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Contact Address';
    }
  },
  OwnerOccupied(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Owner Occupied';
    }
  },
  Stories(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Stories';
    }
  },
  YearBuiltRenovated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Year Built / Renovated';
    }
  },
  LotSize(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lot Size';
    }
  },
  BuildingSize(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Building Size';
    }
  },
  AssessedValue(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Assessed Value';
    }
  },
  Footprint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Footprint';
    }
  },
  Batch(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Batch';
    }
  },
  SolarCharges(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Charges';
    }
  },
  LateCharges(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Late Charges';
    }
  },
  PreviousCharges(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Previous Charges';
    }
  },
  CeaQuote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CEA Quote';
    }
  },
  GenerateNewQuote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generate a new quote';
    }
  },
  GeneratingQuote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generating Quote';
    }
  },
  Proposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Proposal';
    }
  },
  ViewProposalPdf(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'View Proposal PDF';
    }
  },
  GenerateProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generate Proposal';
    }
  },
  GeneratingProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generating Proposal';
    }
  },
  EstimatedYearOneProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Estimated Year 1 Production';
    }
  },
  EstimatedAnnualConsumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Estimated Annual Consumption';
    }
  },
  PeakLoadDemand(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Peak Load Demand';
    }
  },
  Term(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Term';
    }
  },
  Years(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Years';
    }
  },
  YearOneSavings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Year 1 Savings';
    }
  },
  TermSavings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Term Savings';
    }
  },
  Generated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generated';
    }
  },
  Requested(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Requested';
    }
  },
  SetAsActiveProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Set as active proposal';
    }
  },
  RequestCustomProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Request Custom Proposal';
    }
  },
  ProposalRequestedFor(name: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Proposal requested for ${name}`;
    }
  },
  ProjectData(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project Data';
    }
  },
  MoveToTop(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Move to top';
    }
  },
  HelioscopeLink(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Helioscope Link';
    }
  },
  HelioscopeInstructions(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Insert Helioscope project share link or upload Helioscope, PV Syst, or Other PV design report – <b>required in order to generate a Detailed Proposal</b>';
    }
  },
  Documents(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Documents';
    }
  },
  Document(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document';
    }
  },
  RequiredDocuments(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Required Documents';
    }
  },
  ClickToEdit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Click to Edit';
    }
  },
  UtilityBill(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility Bill';
    }
  },
  IntervalData(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Interval Data';
    }
  },
  PvSystem(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PV System';
    }
  },
  OtherPvDesignReport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Other PV Design Report';
    }
  },
  RoofWarranty(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Roof Warranty';
    }
  },
  SiteSurvey(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Site Survey';
    }
  },
  OpenFile(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Open File';
    }
  },
  DeleteDocument(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Delete Document';
    }
  },
  AreYouSureYouWantToDeleteTheDocument(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Are you sure you want to delete the document?';
    }
  },
  AreYouSureYouWantToDeleteTheQuote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Are you sure you want to delete the quote?';
    }
  },
  PreliminaryProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Preliminary Proposal';
    }
  },
  DetailedProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Detailed Proposal';
    }
  },
  SaleCompleted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sale Completed';
    }
  },
  RequestPreliminaryDetailedProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Request Preliminary / Detailed Proposal';
    }
  },
  AreYouSureYouWantToRequestPreliminaryDetailedProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Are you sure you want to request a preliminary / detailed proposal?';
    }
  },
  Credit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Credit';
    }
  },
  Visit(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Visit';
    }
  },
  Copy(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Copy';
    }
  },
  PullSystems(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pull Systems';
    }
  },
  BaseUrl(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Base URL';
    }
  },
  FromFinancialModel(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Parsed from the financial model';
    }
  },
  CashSources(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sources';
    }
  },
  CashUses(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Uses';
    }
  },
  Warnings(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Warnings';
    }
  },
  LuminiaFees(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Luminia Fees';
    }
  },
  GrossItcRevenue(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Gross ITC Revenue';
    }
  },
  NetItcRevenue(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Net ITC Revenue';
    }
  },
  ApiVersion(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'API Version';
    }
  },
  ApiToken(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'API Token';
    }
  },
  Label(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Label';
    }
  },
  FomwareBaseUrlTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'FOMware\'s base URL differs per platform — copy it from the FOMware console. The API token is created there too, and expires.';
    }
  },
  ThankYouForConnectingYourAccountToLuminia(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Thank you for connecting your account to luminia';
    }
  },
  SystemsFound(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Systems Found';
    }
  },
  Created(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Created';
    }
  },
  Import(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Import';
    }
  },
  PowerhubImportTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Upload an export of sites from Powerhub to update existing meters with address and timezone info';
    }
  },
  PpaCharges(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Ppa Charges';
    }
  },
  FixedCharges(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Fixed Charges';
    }
  },
  Credits(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Credits';
    }
  },
  ViewBill(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'View Bill';
    }
  },
  Meter(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Meter';
    }
  },
  BillingPeriod(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Billing Period';
    }
  },
  Tools(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tools';
    }
  },
  CacheProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Cache Production';
    }
  },
  CacheConsumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Cache Consumption';
    }
  },
  // --- Project Management module (prototype) ---
  ProjectManagement(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Project Management'; } },
  Pipeline(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pipeline'; } },
  Stage(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Stage'; } },
  Phase(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Phase'; } },
  AddMilestone(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Add Milestone'; } },
  AddTask(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Add Task'; } },
  Milestone(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Milestone'; } },
  MilestoneRequired(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Milestone is required'; } },
  Details(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Details'; } },
  ResiGen(language?: string) { language = language || getLanguage(); switch (language) { default: return 'ResiGen'; } },
  NewResiGenProject(language?: string) { language = language || getLanguage(); switch (language) { default: return 'New ResiGen Project'; } },
  PrepaymentAmount(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Prepayment Amount'; } },
  SolarSystemSizeKwDc(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Solar PV System Size (kW DC)'; } },
  EssCapacityKwh(language?: string) { language = language || getLanguage(); switch (language) { default: return 'ESS Capacity (kWh)'; } },
  EnergyCommunityAdder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Energy Community Adder'; } },
  DisadvantagedCommunityAdder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Disadvantaged Community Adder'; } },
  ItcStatus(language?: string) { language = language || getLanguage(); switch (language) { default: return 'ITC Status'; } },
  SolarValuation(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Solar Valuation'; } },
  BatteryValuation(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Battery Valuation'; } },
  GrossProjectValue(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Gross Project Value'; } },
  TaxEquityContribution(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tax Equity Contribution'; } },
  RoofEstimate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Roof estimate'; } },
  Panels(language?: string) { language = language || getLanguage(); switch (language) { default: return 'panels'; } },
  ExistingSolarDetected(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Existing solar detected'; } },
  NoRoofData(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No roof data for this address'; } },
  Use(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Use'; } },
  Min(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Min'; } },
  Max(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Max'; } },
  Standard(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Standard'; } },
  ResetToDefaults(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Reset to defaults'; } },
  ValuationTables(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Valuation Tables'; } },
  AllMilestones(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Milestones'; } },
  CompletedFromTasksHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Completed — a milestone completes automatically when all of its tasks are done'; } },
  MoveTasksThenDelete(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Move its tasks, then delete'; } },
  SelectMilestoneForEveryTask(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Select a milestone for every task'; } },
  NoOtherMilestoneForTasks(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No other milestone to move tasks to'; } },
  Vertical(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Vertical'; } },
  Channel(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Channel'; } },
  Tasks(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tasks'; } },
  Task(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Task'; } },
  AiSuggested(language?: string) { language = language || getLanguage(); switch (language) { default: return 'suggested by AI'; } },
  AcceptAll(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Accept all'; } },
  DismissAll(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Dismiss all'; } },
  QuickbooksOutflowSync(language?: string) { language = language || getLanguage(); switch (language) { default: return 'QuickBooks Outflow Sync (Bills)'; } },
  QuickbooksOutflowSyncHelp(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Maps QuickBooks bills to Use cash events. A bill whose number matches an app invoice fills that invoice\'s allocated Uses directly; any other bill line booked to a known project becomes a suggestion to confirm on the Budget tab. What a Use receives is its share of what the bill has actually <b>paid</b> (total less balance), split in proportion to the allocation or the bill\'s lines, and its paid date is set once the bill is settled (an open balance under $1 counts as settled); both keep tracking the bill on every sweep. (Tier 1 — an M0-M6 token on the bill — and Tier 2 — vendor is the EPC and the amount matches an unfilled Use estimate — are currently disabled.) <b>Dry run</b> reports without writing.'; } },
  Bills(language?: string) { language = language || getLanguage(); switch (language) { default: return 'bills'; } },
  Quickbooks(language?: string) { language = language || getLanguage(); switch (language) { default: return 'QuickBooks'; } },
  SortBy(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sort by'; } },
  ProjectPayment(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Project Payment'; } },
  Fees(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Fees'; } },
  Default(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Default'; } },
  Asc(language?: string) { language = language || getLanguage(); switch (language) { default: return 'asc.'; } },
  Desc(language?: string) { language = language || getLanguage(); switch (language) { default: return 'desc.'; } },
  EpcContract(language?: string) { language = language || getLanguage(); switch (language) { default: return 'EPC contract'; } },
  ViewEpcContract(language?: string) { language = language || getLanguage(); switch (language) { default: return 'EPC Contract'; } },
  EpcContractNotUploaded(language?: string) { language = language || getLanguage(); switch (language) { default: return 'The executed EPC contract has not been uploaded yet.'; } },
  ProjectLines(language?: string) { language = language || getLanguage(); switch (language) { default: return 'project lines'; } },
  Unchanged(language?: string) { language = language || getLanguage(); switch (language) { default: return 'unchanged'; } },
  NoProject(language?: string) { language = language || getLanguage(); switch (language) { default: return 'no project'; } },
  RicNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'RIC #'; } },
  TierAction(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tier / action'; } },
  QuickbooksSuggestions(language?: string) { language = language || getLanguage(); switch (language) { default: return 'QuickBooks suggestions'; } },
  NoPendingSuggestions(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No pending suggestions'; } },
  SyncQuickbooksBills(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sync QuickBooks Bills'; } },
  QuickbooksSuggestionsAdded(language?: string) { language = language || getLanguage(); switch (language) { default: return 'QuickBooks suggestion(s) added'; } },
  QuickbooksSyncFailed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'QuickBooks sync failed'; } },
  Confirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Confirm'; } },
  Suggested(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Suggested'; } },
  UnassignedBill(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unassigned bill'; } },
  AssignMilestone(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assign a milestone'; } },
  Dismiss(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Dismiss'; } },
  ReadingWithAi(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Reading with AI'; } },
  ReadingWithAiHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'The AI is reading this document. Any values it finds will appear for review when it finishes.'; } },
  AiReadFailed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'The AI could not read this document'; } },
  ParseWithAi(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Read with AI'; } },
  AiSuggestionsFrom(language?: string) { language = language || getLanguage(); switch (language) { default: return 'read from'; } },
  NoAiSuggestions(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Nothing new found in this document'; } },
  BulkEdit(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Bulk Edit'; } },
  Columns(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Columns'; } },
  ClearColumns(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Clear all'; } },
  BulkImportNeedsProjectId(language?: string) { language = language || getLanguage(); switch (language) { default: return "The first column must be 'Project ID' — re-export the grid and edit that file"; } },
  ExportXlsx(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Export Excel'; } },
  ImportXlsx(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Import Excel'; } },
  ReviewChanges(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Review changes'; } },
  UnknownColumnsIgnored(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unrecognised columns (ignored)'; } },
  NothingToApply(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No differences found'; } },
  ApplyChanges(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Apply changes'; } },
  FillColumn(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Fill column'; } },
  TrackerOrder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tracker order'; } },
  Modeling(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modeling'; } },
  Construction(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Construction'; } },
  Preset(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Preset'; } },
  TextSize(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Text size'; } },
  DragToResize(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Drag to resize · double-click to reset'; } },
  DragToMove(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Drag to move'; } },
  ResetColumnWidths(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Reset widths'; } },
  NameRequired(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Name required'; } },
  TooManyPresets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Too many saved presets — delete one first'; } },
  SavePresetAs(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Save current columns as…'; } },
  PresetName(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Preset name'; } },
  RenamePreset(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Rename'; } },
  DeletePresetConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delete this saved preset? The columns it holds are not affected anywhere else.'; } },
  BuiltIn(language?: string) { language = language || getLanguage(); switch (language) { default: return 'built-in'; } },
  PresetColumnsDropped(language?: string) { language = language || getLanguage(); switch (language) { default: return 'columns in this preset are no longer in the template and were skipped'; } },
  Refresh(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Refresh'; } },
  ApplyToPage(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Fill column on this page'; } },
  Blocking(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Blocking task completion'; } },
  NoColumnsSelected(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pick columns to start'; } },
  TooManyEdits(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Too many edits in one batch'; } },
  NotEditable(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Not editable'; } },
  DocumentsNotBulkEditable(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Documents are uploaded, not typed'; } },
  CellsChanged(language?: string) { language = language || getLanguage(); switch (language) { default: return 'cells changed'; } },
  EditedElsewhere(language?: string) { language = language || getLanguage(); switch (language) { default: return 'edited elsewhere — refresh to see the current value'; } },
  UndoLastBatch(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Undo last change'; } },
  AllStages(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Stages'; } },
  AllPortfolios(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Portfolios'; } },
  AllParameters(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Details'; } },
  // --- Bulk stakeholder management (Team page + Stakeholder modal panel) ---
  Team(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Team'; } },
  ManageAcrossProjects(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Manage across projects'; } },
  Membership(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Membership'; } },
  TaskAssignment(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Task assignment'; } },
  AssignmentMatrix(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Matrix'; } },
  AllStates(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All States'; } },
  Stages(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Stages'; } },
  ExpandFullscreen(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Expand fullscreen'; } },
  Fullscreen(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Fullscreen'; } },
  ExitFullscreen(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Exit fullscreen'; } },
  RoleNotes(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Role/Notes'; } },
  RoleNotesPlaceholder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Role or a note about this stakeholder on this project…'; } },
  AddAllFiltered(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Add all (filtered)'; } },
  RemoveAllFiltered(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Remove all (filtered)'; } },
  MemberOfCount(count: number, total: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `Member of ${count} of ${total}`; } },
  Member(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Member'; } },
  NoProjects(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No projects'; } },
  ApplyRoleHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assign this stakeholder to every task matching a role, across all projects they belong to.'; } },
  SelectRole(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Select a role…'; } },
  AssignToRoleTasks(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assign to role tasks'; } },
  SelectTaskTemplate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Select tasks…'; } },
  SelectTaskTemplateHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Select one or more tasks above to assign them across projects.'; } },
  AssignAllMembers(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assign all'; } },
  UnassignAllMembers(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unassign all'; } },
  NoMemberProjects(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Not a member of any project yet'; } },
  NoSuchTask(language?: string) { language = language || getLanguage(); switch (language) { default: return 'no such task'; } },
  NothingToDo(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Nothing to do'; } },
  RemoveMembershipsConfirm(count: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `Remove this stakeholder from ${count} project(s)? Any tasks they own there will be unassigned.`; } },
  Removed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Removed'; } },
  DeleteProfileConfirm(name: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Delete "${name}"? They will be removed from every project, and any tasks they own will be unassigned.`; } },
  SelectStakeholderHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Select a stakeholder to manage their projects and assignments.'; } },
  DefaultTasks(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Default tasks'; } },
  DefaultTasksHint(name: string, vertical: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `${name} picks up these tasks automatically on every new ${vertical} project, and is made a member of it. After the project exists it is an ordinary assignment, free to change, and a task someone else already owns is never taken over.`; } },
  DefaultTasksEmpty(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No tasks are set as defaults yet. Tick the ones this person should always pick up.'; } },
  AlsoDefaultFor(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Also default for'; } },
  BackfillNow(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Backfill now'; } },
  BackfillDefaultsConfirm(vertical: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Apply these defaults to the ${vertical} projects that already exist? Only tasks nobody owns yet are filled — assignments already made are left alone.`; } },
  BackfillDefaultsResult(tasks: number, projects: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `Assigned ${tasks} task${tasks === 1 ? '' : 's'} across ${projects} project${projects === 1 ? '' : 's'}`; } },
  UnitKw(language?: string) { language = language || getLanguage(); switch (language) { default: return 'kW'; } },
  UnitKwh(language?: string) { language = language || getLanguage(); switch (language) { default: return 'kWh'; } },
  UnitMw(language?: string) { language = language || getLanguage(); switch (language) { default: return 'MW'; } },
  UnitMwh(language?: string) { language = language || getLanguage(); switch (language) { default: return 'MWh'; } },
  UnitGw(language?: string) { language = language || getLanguage(); switch (language) { default: return 'GW'; } },
  UnitGwh(language?: string) { language = language || getLanguage(); switch (language) { default: return 'GWh'; } },
  // --- Excel-style column filters (bulk grid) ---
  Filter(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Filter'; } },
  ClearFilters(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Clear filters'; } },
  ClearFilter(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Clear filter'; } },
  SortAscending(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sort ascending'; } },
  SortDescending(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sort descending'; } },
  Condition(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Condition'; } },
  Values(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Values'; } },
  Blanks(language?: string) { language = language || getLanguage(); switch (language) { default: return '(Blanks)'; } },
  Contains(language?: string) { language = language || getLanguage(); switch (language) { default: return 'contains'; } },
  DoesNotContain(language?: string) { language = language || getLanguage(); switch (language) { default: return 'does not contain'; } },
  IsExactly(language?: string) { language = language || getLanguage(); switch (language) { default: return 'is'; } },
  IsNot(language?: string) { language = language || getLanguage(); switch (language) { default: return 'is not'; } },
  StartsWith(language?: string) { language = language || getLanguage(); switch (language) { default: return 'begins with'; } },
  EndsWith(language?: string) { language = language || getLanguage(); switch (language) { default: return 'ends with'; } },
  GreaterThan(language?: string) { language = language || getLanguage(); switch (language) { default: return 'greater than'; } },
  GreaterOrEqual(language?: string) { language = language || getLanguage(); switch (language) { default: return 'greater than or equal to'; } },
  LessThan(language?: string) { language = language || getLanguage(); switch (language) { default: return 'less than'; } },
  LessOrEqual(language?: string) { language = language || getLanguage(); switch (language) { default: return 'less than or equal to'; } },
  Between(language?: string) { language = language || getLanguage(); switch (language) { default: return 'between'; } },
  IsBlank(language?: string) { language = language || getLanguage(); switch (language) { default: return 'is blank'; } },
  IsNotBlank(language?: string) { language = language || getLanguage(); switch (language) { default: return 'is not blank'; } },
  IsTrue(language?: string) { language = language || getLanguage(); switch (language) { default: return 'is yes'; } },
  IsFalse(language?: string) { language = language || getLanguage(); switch (language) { default: return 'is no'; } },
  Before(language?: string) { language = language || getLanguage(); switch (language) { default: return 'before'; } },
  OnOrBefore(language?: string) { language = language || getLanguage(); switch (language) { default: return 'on or before'; } },
  After(language?: string) { language = language || getLanguage(); switch (language) { default: return 'after'; } },
  OnOrAfter(language?: string) { language = language || getLanguage(); switch (language) { default: return 'on or after'; } },
  HasFile(language?: string) { language = language || getLanguage(); switch (language) { default: return 'has a file'; } },
  HasNoFile(language?: string) { language = language || getLanguage(); switch (language) { default: return 'has no file'; } },
  UnknownColumn(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unknown column'; } },
  TooManyRowsToFilter(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Too many projects to filter — narrow the search first'; } },
  MoreValuesNotShown(language?: string) { language = language || getLanguage(); switch (language) { default: return 'More values exist than can be listed — use a condition instead'; } },
  NoMatchingValues(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No matching values'; } },
  FilteredOfTotal(language?: string) { language = language || getLanguage(); switch (language) { default: return 'of'; } },
  RowsKeptAfterEdit(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Edited rows are kept visible until you refresh'; } },
  TaskWithActualRequiredHint(language?: string) { language = language || getLanguage(); switch (language) { default: return "Task (setting this will make the 'Actual' value required before completing the task)"; } },
  TaskActualsRequired(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Enter a value for these details before completing the task'; } },
  TaskDates(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Dates'; } },
  TaskDateAfterMilestone(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Task dates must be on or before the milestone date'; } },
  Role(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Role'; } },
  VersionHistory(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Version History'; } },
  UploadNewVersion(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Upload New Version'; } },
  Reject(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Reject'; } },
  Revert(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Revert'; } },
  RevertToPending(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Revert to pending'; } },
  ApproveDocConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Approve this document? Its task/milestone may auto-complete.'; } },
  RevertToPendingConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Revert this document to pending? Any task it auto-completed will reopen.'; } },
  Version(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Version'; } },
  Rejected(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Rejected'; } },
  Reason(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Reason'; } },
  RejectedBecause(reason: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Rejected: ${reason}`; } },
  RejectionReasonRequired(language?: string) { language = language || getLanguage(); switch (language) { default: return 'A reason is required to reject a document.'; } },
  Pending(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pending'; } },
  Superseded(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Superseded'; } },
  AddDocument(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Add Document'; } },
  Conditional(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Conditional'; } },
  ClientEmail(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Client Email'; } },
  ClientPhone(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Client Phone'; } },
  UtilityCompany(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Utility Company'; } },
  SgipId(language?: string) { language = language || getLanguage(); switch (language) { default: return 'SGIP ID'; } },
  CurrentVersion(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Current Version'; } },
  Note(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Note'; } },
  Memo(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Memo'; } },
  ChangeNote(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Change note'; } },
  MoveToStage(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Move to stage'; } },
  Download(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Download'; } },
  Invoices(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Invoices'; } },
  InvoicesPayable(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Invoices (payable)'; } },
  UploadInvoice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Upload invoice'; } },
  UploadNewInvoice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Upload a new Invoice'; } },
  ReviewInvoice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Review invoice'; } },
  ReadingInvoice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Reading invoice…'; } },
  InvoiceNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Invoice #'; } },
  LuminiaInvoiceNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Luminia Invoice #'; } },
  EditInvoice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Edit invoice'; } },
  InvoiceAmount(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Invoice amount'; } },
  Allocated(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Allocated to'; } },
  Allocate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Allocate'; } },
  Billed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Billed'; } },
  InvoiceBilledHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Matched to a QuickBooks bill that QuickBooks does not show as settled yet. This is reconciliation, not approval, and only the QuickBooks sync sets it.';
    }
  },
  InvoicePaidHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'QuickBooks shows the matching bill as settled. If that payment is later reversed in QuickBooks, the next sync puts this back to Billed.';
    }
  },
  InvoiceNotBilledHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Not matched to a QuickBooks bill yet. Matching happens on the QuickBooks sync, never by hand.';
    }
  },
  Payment(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Payment'; } },
  AllocationMismatch(language?: string) { language = language || getLanguage(); switch (language) { default: return "doesn't match the invoice amount"; } },
  InvoiceNumberBrianHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Enter this exact number as the Bill no. (DocNumber) in QuickBooks — that is how bills auto-assign to this project.'; } },
  AllocateBrianHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Split this invoice across the M0–M6 payment(s) it covers. When the QuickBooks bill (same Bill no.) is pulled, it auto-fills these uses.'; } },
  NoInvoices(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No invoices uploaded yet.'; } },
  UploadedBy(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Uploaded by'; } },
  RenameInvoice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Rename invoice'; } },
  Rename(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Rename'; } },
  Customer(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Customer'; } },
  Create(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Create'; } },
  AdvanceStage(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Advance stage'; } },
  NoProjectsYet(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No projects yet'; } },
  CapacityKwDc(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Solar (kW DC)'; } },
  StorageKwh(language?: string) { language = language || getLanguage(); switch (language) { default: return 'BESS Capacity (kWh)'; } },
  ExternalId(language?: string) { language = language || getLanguage(); switch (language) { default: return 'External ID'; } },
  EditProject(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Edit Project'; } },
  DeleteProject(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delete Project'; } },
  DeleteProjectConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delete this project and all of its data?'; } },
  DeleteItemConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delete this item?'; } },
  CouldNotLoadProject(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Could not load this project.'; } },
  Override(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Override'; } },
  StageGateConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This stage still has incomplete tasks. Advance anyway?'; } },
  EnterTheStartDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enter the start date';
    }
  },
  BillingEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Billing Email';
    }
  },
  GenerateBills(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generate Bills';
    }
  },
  SendBills(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Send Bills';
    }
  },
  YouAreSendingNumBills(num: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `You are sending ${num} bill${num === 0 ? '' : 's'}`;
    }
  },
  CcEmails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CC Emails';
    }
  },
  CommaSeparated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'comma-separated';
    }
  },
  Test(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Test';
    }
  },
  AccessCode(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Access Code';
    }
  },
  BillingAccessCode(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Billing Access Code';
    }
  },
  EnterYourAccessCode(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enter Your Access Code';
    }
  },
  YourSolarEnergyBills(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Your Solar Energy Bills';
    }
  },
  ConsolidateBill(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Consolidate Bill';
    }
  },
  Sending(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sending';
    }
  },
  Verified(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Verified';
    }
  },
  ApiKey(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Api Key';
    }
  },
  ApiUrl(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'API URL';
    }
  },
  KeyId(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Key ID';
    }
  },
  KeySecret(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Key Secret';
    }
  },
  ProductionReport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Production Report';
    }
  },
  MetaReport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Meta Report';
    }
  },
  Issues(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Issues';
    }
  },
  Pull(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pull';
    }
  },
  FindGaps(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Find Gaps';
    }
  },
  SiteLease(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Site Lease';
    }
  },
  RateCommitment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rate Commitment';
    }
  },
  Send(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Send';
    }
  },
  Required(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Required';
    }
  },
  GenerateQuote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generate Quote';
    }
  },
  Export(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Export';
    }
  },
  ExportProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Export Production';
    }
  },
  ExportConsumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Export Consumption';
    }
  },
  PleaseSelectMetersToExport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please select meters to export';
    }
  },
  UseUtcFormat(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Use UTC time';
    }
  },
  GeneratePdf(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generate PDF';
    }
  },
  Recipients(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Recipients';
    }
  },
  RecipientsAreRequired(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Recipients are required';
    }
  },
  SolarPlusBusinessPortion(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Plus Business Portion';
    }
  },
  UtilityPortion(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility Portion';
    }
  },
  AssignedTo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Assigned To';
    }
  },
  ProjectNameIsRequired(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project name is required';
    }
  },
  AgreementInformation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Agreement Information';
    }
  },
  GenerateAgreements(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generate Agreements';
    }
  },
  LessorEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessor Email';
    }
  },
  LessorName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessor Name';
    }
  },
  LessorCompany(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessor Company';
    }
  },
  LessorCompanyParent(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessor Company Parent';
    }
  },
  LessorAddress1(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessor Address1';
    }
  },
  LessorAddress2(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessor Address2';
    }
  },
  LessorAttentionName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessor Attention Name';
    }
  },
  LessorAttentionEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessor Attention Email';
    }
  },
  LesseeEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessee Email';
    }
  },
  LesseeName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessee Name';
    }
  },
  LesseeCompany(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessee Company';
    }
  },
  LesseeCompanyParent(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lessee Company Parent';
    }
  },
  PropertyOwnerEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Property Owner Email';
    }
  },
  PropertyOwnerName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Property Owner Name';
    }
  },
  TenantEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tenant Email';
    }
  },
  TenantName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Tenant Name';
    }
  },
  CeaEmail(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CEA Email';
    }
  },
  CeaName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CEA Name';
    }
  },
  LeaseEffectiveDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lease Effective Date';
    }
  },
  MeterNumbers(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Meter Numbers';
    }
  },
  PtoDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PTO Date';
    }
  },
  InitialRate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Initial Rate';
    }
  },
  InitialRateUpdated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Initial Rate Updated';
    }
  },
  TermEndDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Term End Date';
    }
  },
  RenewalTermYears(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Renewal Term (years)';
    }
  },
  MaxTermEndDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Max. Term End Date';
    }
  },
  FirstRentPayment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'First Rent Payment';
    }
  },
  ProCo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pro c/o';
    }
  },
  PropertyLegalDescription(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Property Legal Description';
    }
  },
  EasementsMetesAndBounds(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Easement Metes and Bounds';
    }
  },
  CustomerUtilityMeterNumbers(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Customer Utility Meter Numbers';
    }
  },
  PropertyApn(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Property APN';
    }
  },
  AgreementExpirationDate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Agreement Expiration Date';
    }
  },
  SendForSigning(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Send for Signing';
    }
  },
  AddOrModifyAccountsInYourOrg(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add or modify User Accounts in your Organization';
    }
  },
  Indicative(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Indicative';
    }
  },
  Detailed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Detailed';
    }
  },
  YouMustUploadConsumptionDataIntheDataroomToGenerateDetailedProposals(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'You must upload consumption data in the Dataroom tab to generate a Detailed Quote';
    }
  },
  QuoteStatusTip(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return '<span>Most recent proposal has status default to active. To update, use the <span style="font-size: 2rem; position: relative; top: 0.25rem;">⏻</span> icon under actions.</span>';
    }
  },
  Actions(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Actions';
    }
  },
  OneYearFifteenMinuteUsageData(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return '1 year of 15-minute usage data';
    }
  },
  HelioscopeReport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Helioscope Report';
    }
  },
  AvoidedCostAnalysis(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Avoided Cost Analysis';
    }
  },
  EpcQuote(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC Bid/Quote';
    }
  },
  Uploaded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Uploaded';
    }
  },
  Approved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Approved';
    }
  },
  Approve(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Approve';
    }
  },
  UtilityAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility Account';
    }
  },
  UtilityAccounts(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility Accounts';
    }
  },
  UtilityIntakeDisclaimerMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please enter your details below to securely share your utility data with Luminia';
    }
  },
  UtilityIntakeDisclaimer(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `By providing your utility account login details, you agree to authorize Luminia 
      to login to your account and share utility data with Luminia, and you agree to Luminia's<br />
      <a href="https://luminia.io/privacy-policy/" target="_blank">Privacy Policy</a> and
      <a href="https://luminia.io/terms-of-service/" target="_blank">Terms of Service</a>`;
    }
  },
  UtilityIntakeDoneMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return '<p>Thank you, we&rsquo;re pleased to confirm receipt of your utility data submission. Our team will now begin reviewing the utility information as part of the project analysis process. We appreciate your timely submission and look forward to advancing the opportunity.</p><p>If you have any questions, concerns, or requests, regarding this utility data submission, please reach out at <a href="mailto:technology@luminia.io">technology@luminia.io</a>.</p>';
    }
  },
  OtherDocuments(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Other Documents';
    }
  },
  RequiredDocumentsInstructions(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The following documents/items in the table below are required in order to generate a Detailed Proposal';
    }
  },
  UploadUsageData(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Upload Usage Data';
    }
  },
  CopyLinkToConnectUtilityAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Copy a link that you can send to a customer to guide them through sharing their utility data with Luminia';
    }
  },
  CopiedToClipboard(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Copied to Clipboard';
    }
  },
  CopyProjectLinkHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Click to copy a link to this project';
    }
  },
  ConsumptionMeters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Consumption Meters';
    }
  },
  ConsumptionMeter(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Consumption Meter';
    }
  },
  TwelveMonthUsage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return '12-month Usage (kWh)';
    }
  },
  Compile12MonthUsage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Compile 12-month Usage';
    }
  },
  ConnectingYourAccount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Connecting your account. Please wait. This may take several minutes.';
    }
  },
  AccessExpires(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Access Expires (leave blank to allow access indefinitely)';
    }
  },
  Expires(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Expires';
    }
  },
  Never(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Never';
    }
  },
  CcaServiceTerritory(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CCA Service Territory';
    }
  },
  SystemSizeLessThan100Message(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PV system must be 100 kW DC or greater';
    }
  },
  EssCapacityLessThan200Message(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS capacity must be 200 kWh or greater';
    }
  },
  SystemSizeLessThan80PercentEssSizeMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PV system must be at least 80% of the ESS size';
    }
  },
  AvoidedCostGreaterThan40Message(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Avoided Cost must be $0.40/kWh or less.';
    }
  },
  EssAvoidedCostGreaterThan300Message(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Avoided Cost must be $300/kWh or less.';
    }
  },
  EpcPriceOutsideRangeMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC cost must be between $2/W and $7/W.';
    }
  },
  EssEpcPriceOutsideRangeMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'ESS Install cost must be between $200/kWh and $1250/kWh.';
    }
  },
  PvOffsetTooHightMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PV offset (annual production divided by annual consumption) must be no greater than 60%';
    }
  },
  AnnualConsumptionTooLowMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Annual consumption must be greater than 250,000 kWh';
    }
  },
  Validate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Validate';
    }
  },
  PleaseRequestCustomProposalMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please request a custom proposal to override system constraints';
    }
  },
  ChangeImage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Change Image';
    }
  },
  AreYouSureYouWouldLikeToRequestCustomProposal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Are you sure you would like to request a custom proposal?';
    }
  },
  YouHaveRequestedCustomProposalMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'You have requested a custom proposal. A notification has been sent to the Luminia team. Thank you.';
    }
  },
  PullConsumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pull Consumption';
    }
  },
  SendToProject(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Send to Project';
    }
  },
  SendToProjectInstruction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Select the Project that will use this meter(s) for the estimated annual consumption and interval data in the Data Room.';
    }
  },
  OneOrMoreMetersHaveNoData(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'One or more meters have no data';
    }
  },
  QuoteParametersGenerateNegiveSavingsMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Quote parameters generate negative savings to customer. Please request a custom proposal to override system constraints.';
    }
  },
  ProcessSdgeIntervals(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Process SDG&E Intervals';
    }
  },
  ProcessSdgeBills(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Process SDG&E Bills';
    }
  },
  PullBills(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Pull Bills';
    }
  },
  UtilityBills(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility Bills';
    }
  },
  Charges(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Charges';
    }
  },
  NetConsumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Net Consumption';
    }
  },
  Meters(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Meters';
    }
  },
  Current(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Current';
    }
  },
  Forecasted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Forecasted';
    }
  },
  SolarRevenue(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Revenue';
    }
  },
  Systems(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Systems';
    }
  },
  System(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'System';
    }
  },
  // --- Left nav groups ---
  Dashboard(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Dashboard'; } },
  Ric(language?: string) { language = language || getLanguage(); switch (language) { default: return 'RIC'; } },
  Prospector(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Prospector'; } },
  ProjectDevelopment(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Project Development'; } },
  InventoryManagement(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inventory Management'; } },
  DataHub(language?: string) { language = language || getLanguage(); switch (language) { default: return 'DataHub'; } },
  ProductionData(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Production Data'; } },
  ConsumptionData(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Consumption Data'; } },
  Billing(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Billing'; } },
  CeaBills(language?: string) { language = language || getLanguage(); switch (language) { default: return 'CEA Bills'; } },
  Generate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Generate'; } },
  TodaysProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Today\'s Production';
    }
  },
  TodaysRevenue(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Today\'s Revenue';
    }
  },
  YtdProduction(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'YTD Production';
    }
  },
  YtdRevenue(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'YTD Revenue';
    }
  },
  ForecastedRevenue(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Forecasted Revenue';
    }
  },
  CustomerName(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Customer Name';
    }
  },
  FormattedAddressOverride(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Formatted Address Override';
    }
  },
  ExportToCsv(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Export to CSV';
    }
  },
  UtilityLink(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility Link';
    }
  },
  Consumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Consumption';
    }
  },
  TotalConsumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total Consumption';
    }
  },
  ViewConsumption(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'View Consumption';
    }
  },
  PtoYear(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PTO Year';
    }
  },
  DropToUpload(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Drop to upload';
    }
  },
  FileTooLarge(name: string, size: string, max: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${name} is ${size}. The maximum upload size is ${max}.`;
    }
  },
  ExecutableUploadBlocked(name: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${name} is a program (.exe). Programs can't be uploaded here.`;
    }
  },
  SolarEpcCostItcEligible(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar EPC Cost ITC Eligible';
    }
  },
  SolarEnergyCommunityAdder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Energy Community Adder';
    }
  },
  SolarDomesticContentAdder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar Domestic Content Adder';
    }
  },
  StorageEpcCostItcEligible(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Storage EPC Cost ITC Eligible';
    }
  },
  StorageEnergyCommunityAdder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Storage Energy Community Adder';
    }
  },
  StorageDomesticContentAdder(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Storage Domestic Content Adder';
    }
  },
  SiteLeasePayment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Site Lease Payment';
    }
  },
  Minimal(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Minimal';
    }
  },
  Full(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Full';
    }
  },
  LatestBill(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Latest Bill';
    }
  },
  Start(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Start';
    }
  },
  End(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'End';
    }
  },
  Amount(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Amount';
    }
  },
  Website(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Website';
    }
  },
  YearOneLeaseIncome(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Year 1 Lease Income';
    }
  },
  TermLeaseIncome(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Term Lease Income';
    }
  },
  SolarPvSystemSize(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Solar PV System Size';
    }
  },
  GenerateUtilityDataLink(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Generate Utility Data Link';
    }
  },
  PleaseEditThisProjectToUpdateTheEpcPrice(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Please edit this project to update the EPC price';
    }
  },
  Interconnections(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Interconnections';
    }
  },
  Interconnection(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Interconnection';
    }
  },
  ConfigureInterconnections(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Configure Interconnections';
    }
  },
  Weight(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Weight';
    }
  },
  Add(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add';
    }
  },
  NoInterconnections(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No interconnections';
    }
  },
  Layers(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Layers';
    }
  },
  Loading(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Loading';
    }
  },
  Added(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Added';
    }
  },
  UploadFile(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Upload File';
    }
  },
  UseNearmap(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Use Nearmap';
    }
  },
  UseIca(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Use ICA';
    }
  },
  DataExport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Data Export';
    }
  },
  MonthlyCeaProgramRate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Monthly CEA Program Rate';
    }
  },
  CreatingAccountInOrg(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Creating account in Org';
    }
  },
  AdditionalInputs(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Additional Inputs';
    }
  },
  MinimumRoofArea(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Minimum Roof Area';
    }
  },
  MaximumRoofArea(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Maximum Roof Area';
    }
  },
  LimitResults(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Limit Results';
    }
  },
  IncludeResidential(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Include Residential';
    }
  },
  Apply(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Apply';
    }
  },
  Rectangle(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rectangle';
    }
  },
  Circle(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Circle';
    }
  },
  DrawingHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Right-click to draw, Esc to cancel';
    }
  },
  Measure(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Measure';
    }
  },
  MeasureHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Click to add points, double-click to close the shape, Esc to start over';
    }
  },
  Undo(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Undo';
    }
  },
  MeasurementLength(feet: string, miles: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${feet} ft · ${miles} mi`;
    }
  },
  MeasurementArea(squareFeet: string, acres: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${squareFeet} ft² · ${acres} acres`;
    }
  },
  MeasurementAreaWide(squareFeet: string, acres: string, squareMiles: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${squareFeet} ft² · ${acres} acres · ${squareMiles} mi²`;
    }
  },
  FootprintsFound(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${count} footprints found`;
    }
  },
  AreYouSureYouWantToDeleteTheProspect(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Are you sure you want to delete the prospect';
    }
  },
  FootprintDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Footprint Details';
    }
  },
  FootprintArea(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Footprint Area';
    }
  },
  AddToBatch(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add to Batch';
    }
  },
  UnableToGeocodeFootprint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Unable to geocode footprint';
    }
  },
  EnrichingProgress(completed: number, total: number, language?: string) {
    language = language || getLanguage();
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    switch (language) {
      default: return `Enriching ${completed}/${total} (${pct}%)`;
    }
  },
  AddAll(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Add All';
    }
  },
  AddingProgress(completed: number, total: number, language?: string) {
    language = language || getLanguage();
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    switch (language) {
      default: return `Adding ${completed}/${total} (${pct}%)`;
    }
  },
  Keep(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Keep';
    }
  },
  Utilities(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utilities';
    }
  },
  Ccas(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CCAs';
    }
  },
  CalCca(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CalCCA';
    }
  },
  CecCca(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'CEC CCA';
    }
  },
  Apns(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'APNs';
    }
  },
  Footprints(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Footprints';
    }
  },
  Lmi(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'LMI';
    }
  },
  Dac(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'DAC';
    }
  },
  Msa(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'MSA';
    }
  },
  Substations(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Substations';
    }
  },
  Circuits(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Circuits';
    }
  },
  CouldNotLoadLayer(layer: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Could not load the ${layer} layer`;
    }
  },
  ShowingPartOfLayer(layer: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Showing part of the ${layer} layer — zoom in for the rest`;
    }
  },
  LmiShareOfPopulation(percent: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${percent}% low-to-moderate income`;
    }
  },
  LmiPeople(people: string, population: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${people} of ${population} people`;
    }
  },
  DisadvantagedCommunity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Disadvantaged community';
    }
  },
  DacBurdens(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Burdens';
    }
  },
  DacCategoryClimate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Climate';
    }
  },
  DacCategoryEnergy(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy';
    }
  },
  DacCategoryHealth(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Health';
    }
  },
  DacCategoryHousing(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Housing';
    }
  },
  DacCategoryLegacyPollution(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Legacy pollution';
    }
  },
  DacCategoryTransportation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Transportation';
    }
  },
  DacCategoryWorkforce(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Workforce';
    }
  },
  DacCategoryWater(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Water';
    }
  },
  EnergyCommunity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Energy community';
    }
  },
  FossilFuelEmployment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Fossil fuel employment';
    }
  },
  ExistingGeneration(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Existing generation';
    }
  },
  QueuedGeneration(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Queued generation';
    }
  },
  ProjectedLoad(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Projected load';
    }
  },
  PenetrationLevel(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Penetration';
    }
  },
  RemainingCapacity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Remaining capacity';
    }
  },
  Substation(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Substation';
    }
  },
  PvCapacity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PV capacity';
    }
  },
  PvCapacityWithCurtailment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'PV capacity, curtailable';
    }
  },
  LoadCapacity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Load capacity';
    }
  },
  MostLimitingFactor(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Limited by';
    }
  },
  Megawatts(value: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${value} MW`;
    }
  },
  Percentage(value: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${value}%`;
    }
  },
  LayerPartlyUnavailable(layer: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Part of the ${layer} layer could not be loaded`;
    }
  },
  ConnectedGeneration(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Connected generation';
    }
  },
  TotalGeneration(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Total generation';
    }
  },
  HeadroomEstimate(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Headroom (estimate)';
    }
  },
  Customers(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Customers';
    }
  },
  Residential(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Residential';
    }
  },
  Commercial(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Commercial';
    }
  },
  Industrial(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Industrial';
    }
  },
  Agricultural(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Agricultural';
    }
  },
  ViewReport(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'View Report';
    }
  },
  Activity(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Activity';
    }
  },
  Timeline(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Timeline';
    }
  },
  Schedule(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Schedule';
    }
  },
  Gantt(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Gantt';
    }
  },
  Calendar(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Calendar';
    }
  },
  NoActivityYet(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No activity yet';
    }
  },
  ProjectCreated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project created';
    }
  },
  ProjectDeleted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Project deleted';
    }
  },
  AssigneeChanged(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Assignee changed';
    }
  },
  StatusChanged(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Status changed';
    }
  },
  FieldsEdited(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Fields edited';
    }
  },
  EnrichmentStarted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enrichment started';
    }
  },
  EnrichmentCompleted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enrichment completed';
    }
  },
  EnrichmentFailed(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enrichment failed';
    }
  },
  EnrichmentStep(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enrichment step';
    }
  },
  DocumentAdded(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document added';
    }
  },
  DocumentApproved(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document approved';
    }
  },
  DocumentDeleted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Document deleted';
    }
  },
  QuoteCreated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Quote created';
    }
  },
  QuoteUpdated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Quote updated';
    }
  },
  QuoteDeleted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Quote deleted';
    }
  },
  ConsumptionMetersApplied(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Consumption meters applied';
    }
  },
  HubspotPortalRecordCreated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'HubSpot record created';
    }
  },
  EpcPriceModelRun(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'EPC price model run';
    }
  },
  ActivityFilterAll(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'All';
    }
  },
  ActivityFilterLifecycle(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Lifecycle';
    }
  },
  ActivityFilterEnrichment(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Enrichment';
    }
  },
  ActivityFilterDocuments(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Documents';
    }
  },
  ActivityFilterEdits(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Edits';
    }
  },
  ActivityFilterIntegrations(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Integrations';
    }
  },
  SystemActor(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'System';
    }
  },
  JobActor(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Job';
    }
  },
  ShowDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Show details';
    }
  },
  HideDetails(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Hide details';
    }
  },
  View(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'View';
    }
  },
  RooftopArea(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rooftop Area';
    }
  },
  RooftopAreaSource(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Rooftop Area Source';
    }
  },
  BuildingTypes(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Building Types';
    }
  },
  Reonomy(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Reonomy';
    }
  },
  MaxSystemSize(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Max System Size';
    }
  },
  ExistingSolar(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Existing Solar';
    }
  },
  Sunroof(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Sunroof';
    }
  },
  GmapsDestination(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Google Maps Destination';
    }
  },
  ScaleSystemSize(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Scale System Size';
    }
  },
  HubspotPortalRecord(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'HubSpot Portal Record';
    }
  },
  MaxDemand(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Max Demand';
    }
  },
  Utility(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Utility';
    }
  },
  UtilityLoginCredentialsErrorMessage(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'An error occurred. Please ensure that your username and password are correct.';
    }
  },
  Today(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Today';
    }
  },
  PreviousMonth(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Previous month';
    }
  },
  NextMonth(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Next month';
    }
  },
  NoDatedItems(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Nothing on the calendar yet — tasks appear once they have an estimated or actual date.';
    }
  },
  UndatedTasksCount(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `${count} ${count === 1 ? 'task' : 'tasks'} with no date`;
    }
  },
  MoreCount(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `+${count} more`;
    }
  },
  ScheduleLegendEstimated(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Estimated task date';
    }
  },
  ScheduleLegendActual(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Actual task date';
    }
  },
  ScheduleLegendMilestone(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Milestone';
    }
  },
  MilestoneCompleted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Milestone completed';
    }
  },
  SelectThisMonth(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Show this month';
    }
  },
  // --- Inventory Management ---
  InventoryStatusOnOrder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'On Order (Inbound)'; } },
  InventoryStatusInStock(language?: string) { language = language || getLanguage(); switch (language) { default: return 'In Stock'; } },
  InventoryStatusReserved(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Reserved'; } },
  InventoryStatusAllocated(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Allocated'; } },
  InventoryStatusStaged(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Staged for Shipment'; } },
  InventoryStatusInTransit(language?: string) { language = language || getLanguage(); switch (language) { default: return 'In Transit'; } },
  InventoryStatusDelivered(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delivered'; } },
  InventoryStatusInstalled(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Installed'; } },
  InventoryStatusDamaged(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Damaged'; } },
  InventoryStatusRmaReturn(language?: string) { language = language || getLanguage(); switch (language) { default: return 'RMA / Return'; } },
  InventoryStatusLost(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Lost'; } },
  InventoryDeliveryNotRequested(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Not Requested'; } },
  InventoryDeliveryRequested(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Requested'; } },
  InventoryDeliveryScheduled(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Scheduled'; } },
  InventoryDeliveryStaged(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Staged'; } },
  InventoryDeliveryException(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Exception'; } },
  InventoryShipmentDelayed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delayed'; } },
  InventoryAllocationPartial(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Partial'; } },
  InventoryAllocationUnallocated(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unallocated'; } },
  InventoryFeocNon(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Non-FEOC'; } },
  InventoryFeocYes(language?: string) { language = language || getLanguage(); switch (language) { default: return 'FEOC'; } },
  InventoryFeocTbd(language?: string) { language = language || getLanguage(); switch (language) { default: return 'TBD'; } },
  InventoryDirectionInbound(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inbound'; } },
  InventoryDirectionOutbound(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Outbound'; } },
  InventoryDirectionTransfer(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Transfer'; } },
  InventoryWarehouse(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Warehouse'; } },
  InventoryWarehouses(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Warehouses'; } },
  InventoryLot(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Purchase Lot'; } },
  InventoryLots(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Lots & POs'; } },
  InventorySite(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Site'; } },
  InventorySites(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sites'; } },
  InventoryPo(language?: string) { language = language || getLanguage(); switch (language) { default: return 'PO'; } },
  InventoryPallet(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallet'; } },
  InventoryPalletInventory(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallet Inventory'; } },
  InventoryDeliveries(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Deliveries'; } },
  InventoryDelivery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delivery'; } },
  InventoryShipments(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Shipments'; } },
  InventoryShipment(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Shipment'; } },
  InventorySerials(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Module Serials'; } },
  InventoryImport(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Import Tracker'; } },
  InventoryPalletNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallet #'; } },
  InventoryProvisional(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Provisional'; } },
  InventoryContractNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Contract #'; } },
  InventoryModules(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules'; } },
  InventoryModulesPerPallet(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules per Pallet'; } },
  InventoryWattage(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wattage'; } },
  InventoryModuleModel(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Module Model'; } },
  InventoryUnitCost(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unit Cost'; } },
  InventoryPalletValue(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallet Value'; } },
  InventoryAssignedSite(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assigned Site'; } },
  InventoryAssignDate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assigned'; } },
  InventoryExpectedStatus(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Expected Status'; } },
  InventorySerialsOnFile(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Serials on File'; } },
  InventoryWarrantyExpiration(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Warranty Expiration'; } },
  InventoryModulesOrdered(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules Ordered'; } },
  InventoryPalletsOrdered(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Ordered'; } },
  InventoryBasePrice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Base Price'; } },
  InventorySalesTax(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sales Tax'; } },
  InventoryTotalCost(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Total Cost'; } },
  InventoryLandedUnitPrice(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Price per Unit at Purchase'; } },
  InventoryBookUnits(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Book Value (units)'; } },
  InventoryBookValue(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Book Value'; } },
  InventoryModulesAllocated(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules Allocated'; } },
  InventoryModulesRemaining(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules Remaining'; } },
  InventoryPalletsAllocated(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Allocated'; } },
  InventoryPalletsRemaining(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Remaining'; } },
  InventoryPricePerWatt(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Price per Watt'; } },
  InventorySalesTaxRate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sales Tax Rate'; } },
  InventoryEdd(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Earliest Delivery Date'; } },
  InventoryGdd(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Guaranteed Delivery Date'; } },
  InventoryPaymentTerms(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Payment Terms'; } },
  InventoryShippingTerms(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Shipping Terms'; } },
  InventoryDomesticContent(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Domestic Content'; } },
  InventoryFeocStatus(language?: string) { language = language || getLanguage(); switch (language) { default: return 'FEOC Status'; } },
  InventoryVendorInvoiceNumbers(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Vendor Invoice #'; } },
  InventoryShipTo(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Ship To'; } },
  InventoryCostBasisUnknown(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No documented cost basis'; } },
  InventorySiteId(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Site ID'; } },
  InventoryTenant(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tenant'; } },
  InventoryPropertyName(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Property'; } },
  InventoryRegion(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Region'; } },
  InventoryClosestWarehouse(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Closest Warehouse'; } },
  InventoryGroup(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Group'; } },
  InventoryLntpSize(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Work Order Size'; } },
  InventoryLntpAmount(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Work Order Amount'; } },
  InventoryEquipmentWoSize(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Equipment WO Size'; } },
  InventoryEquipmentWoAmount(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Equipment WO Amount'; } },
  InventoryNoEquipmentWo(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No Equipment WO'; } },
  InventoryFinalDesignSize(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Final Design Size'; } },
  InventoryDesignSize(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Design Size'; } },
  InventoryDesignSizeOverride(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Design Size Override'; } },
  InventoryModulesRequired(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules Required'; } },
  InventoryPalletsRequired(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Required'; } },
  InventoryModuleBalance(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Module Balance'; } },
  InventoryAllocationStatus(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Allocation'; } },
  InventoryTrucks(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Trucks'; } },
  InventoryTrucksRequired(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Trucks Required'; } },
  InventoryModulesForDesign(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules at Current Design'; } },
  InventorySizing(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sizing'; } },
  InventoryNotInProjectManagement(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Not in Project Management'; } },
  InventoryMobilizationDate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Scheduled Mobilization'; } },
  InventoryShipDate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Actual Ship Date'; } },
  InventoryArrivedAt(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Arrived at Job Site'; } },
  InventoryReceivedBy(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Received By'; } },
  InventoryArrivalVsMobilization(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Arrival vs Mobilization'; } },
  InventoryRequestedBy(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Requested By'; } },
  InventoryRequestedAt(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Requested'; } },
  InventoryArrivesAfterMobilization(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Arrives after mobilization'; } },
  InventoryCapacity(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Storage Capacity'; } },
  InventoryUtilization(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Utilization'; } },
  InventoryOperator(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Operator'; } },
  InventoryOperatorProjectNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Operator Project #'; } },
  InventoryOperatorWarehouseName(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Operator Site Name'; } },
  InventoryReceivingWindow(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Receiving Window'; } },
  InventoryLaydownYard(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Laydown Yard'; } },
  InventoryStorageType(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Storage'; } },
  InventoryBonded(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Bonded'; } },
  InventoryScheduleAPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Contract Pallet Allowance'; } },
  InventoryScheduleAMw(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Contract MW Allowance'; } },
  InventoryInFee(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inbound Fee per Pallet'; } },
  InventoryOutFee(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Outbound Fee per Pallet'; } },
  InventoryStoragePerPalletMonth(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Storage per Pallet-Month'; } },
  InventoryScanFee(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallet Scan Fee'; } },
  InventoryDunnagePerTruck(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Dunnage per Truck'; } },
  InventoryLaborPerHour(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Labor per Hour'; } },
  InventoryOvertimePerHour(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Overtime per Hour'; } },
  InventoryMonthlyMinimum(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Monthly Minimum'; } },
  InventoryEscalator(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Escalator'; } },
  InventoryEscalatorEffective(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Escalator Effective'; } },
  InventoryRates(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Contract Rates'; } },
  InventoryLineItems(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Line Items'; } },
  InventoryReceipt(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Warehouse Receipt'; } },
  InventoryTransNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Transaction #'; } },
  InventoryDateReceived(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Date Received'; } },
  InventoryShipmentRef(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Shipment Ref'; } },
  InventoryContainer(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Container #'; } },
  InventoryBol(language?: string) { language = language || getLanguage(); switch (language) { default: return 'BOL #'; } },
  InventoryReceiveItemId(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Receive Item ID'; } },
  InventoryCuFt(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Cubic Feet'; } },
  InventoryLbs(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Weight (lbs)'; } },
  InventoryHold(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Hold'; } },
  InventoryFlashTest(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Flash Test'; } },
  InventoryFlashPmax(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pmax'; } },
  InventoryVoc(language?: string) { language = language || getLanguage(); switch (language) { default: return 'VOC'; } },
  InventoryIsc(language?: string) { language = language || getLanguage(); switch (language) { default: return 'ISC'; } },
  InventoryVm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'VM'; } },
  InventoryIm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'IM'; } },
  InventoryFillFactor(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Fill Factor'; } },
  InventoryPosition(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Position'; } },
  InventoryNameplate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Nameplate'; } },
  InventoryMeanPmax(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Mean Pmax'; } },
  InventoryBelowNameplate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Below Nameplate'; } },
  InventoryPlant(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Plant'; } },
  InventoryDeliveryNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delivery #'; } },
  InventoryMfgDate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Manufactured'; } },
  InventorySerialNumber(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Serial Number'; } },
  InventoryShipmentId(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Shipment ID'; } },
  InventoryOrigin(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Origin'; } },
  InventoryDestination(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Destination'; } },
  InventoryCarrier(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Carrier'; } },
  InventoryEquipmentSummary(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Equipment'; } },
  InventoryExpectedDelivery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Expected Delivery'; } },
  InventoryInboundDate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Arrived at Warehouse'; } },
  InventoryOutboundDate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Left Origin'; } },
  InventoryReductions(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inventory Reductions'; } },
  InventoryReductionAmount(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inventory Reduction'; } },
  InventoryTaxJurisdiction(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tax Jurisdiction'; } },
  InventorySalesTaxPaid(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sales Tax Paid'; } },
  InventoryUseTaxDue(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Use Tax Due'; } },
  InventoryUseTaxRate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Use Tax Rate'; } },
  InventoryTaxLiability(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tax Liability'; } },
  InventoryPaidDate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Paid Date'; } },
  InventoryCountColumn(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Count'; } },
  InventoryInvoices(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Warehouse Invoices'; } },
  InventoryInvoiceTotal(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Invoice Total'; } },
  InventoryBalanceDue(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Balance Due'; } },
  InventoryServicePeriod(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Service Period'; } },
  InventoryAmountPaid(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Amount Paid'; } },
  InventoryUnpaid(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unpaid'; } },
  InventoryAssignToSite(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assign to Site'; } },
  InventoryUnassign(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unassign'; } },
  InventoryAssignSelected(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assign Selected'; } },
  InventoryUnassignSelected(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unassign Selected'; } },
  InventoryClearSelection(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Clear'; } },
  InventoryRequestDelivery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Request Delivery'; } },
  InventoryMarkDamaged(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Mark Damaged'; } },
  InventoryMarkRma(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Mark RMA / Return'; } },
  InventoryMarkLost(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Mark Lost'; } },
  InventoryReturnToStock(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Return to Stock'; } },
  InventoryRenamePallet(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Set Pallet Number'; } },
  InventoryFillRequirement(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Fill Requirement'; } },
  InventoryOpenInPalletInventory(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Open in Pallet Inventory'; } },
  InventoryAdvanceDelivery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Advance Delivery'; } },
  InventoryDeleteDelivery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delete Delivery'; } },
  InventoryApplyImport(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Import'; } },
  InventoryImportResults(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Import Results'; } },
  InventoryTieOut(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tie-Out'; } },
  InventoryWarnings(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Warnings'; } },
  InventoryErrors(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Errors'; } },
  InventorySelectWorkbook(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Select the tracker workbook'; } },
  InventoryViewSerials(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Serials'; } },
  InventoryViewEvents(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Activity'; } },
  InventoryAllLots(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Lots'; } },
  InventoryAllWarehouses(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Warehouses'; } },
  InventoryAllStatuses(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Statuses'; } },
  InventoryAllRegions(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Regions'; } },
  InventoryAllGroups(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Groups'; } },
  InventoryAllPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Pallets'; } },
  InventoryAssignedOnly(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Assigned Only'; } },
  InventoryUnassignedOnly(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unassigned Only'; } },
  InventoryAllSiteStatuses(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Site Statuses'; } },
  InventoryLinkedOnly(language?: string) { language = language || getLanguage(); switch (language) { default: return 'In Project Management'; } },
  InventoryUnlinkedOnly(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Not in Project Management'; } },
  InventoryAllAllocations(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Allocation States'; } },
  InventoryAllDeliveryStates(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Delivery States'; } },
  InventoryNoDelivery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No Delivery'; } },
  InventoryAllDirections(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Directions'; } },
  InventorySearchPalletsPlaceholder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallet #, PO, contract, site, note'; } },
  InventorySearchSitesPlaceholder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Site ID, tenant, property, group'; } },
  InventorySearchSerialsPlaceholder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Serial, pallet #, container, BOL, batch'; } },
  InventorySearchShipmentsPlaceholder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Shipment ID, PO, BOL, destination'; } },
  InventoryPortfolioSnapshot(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Portfolio Snapshot'; } },
  InventoryTotalUnits(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Total Units'; } },
  InventoryUnitsOnHand(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Units On Hand'; } },
  InventoryUnitsOnOrder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Units On Order'; } },
  InventoryTotalValue(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Total Value'; } },
  InventoryOnHandValue(language?: string) { language = language || getLanguage(); switch (language) { default: return 'On-Hand Value'; } },
  InventoryTotalPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Total Pallets'; } },
  InventoryProcurement(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Procurement / Safe Harbor'; } },
  InventoryTotalModules(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Total Modules'; } },
  InventoryTotalNameplate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Total Nameplate'; } },
  InventoryCostBasisExTax(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Cost Basis ex-tax'; } },
  InventoryGrandTotalInclTax(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Grand Total incl. tax'; } },
  InventoryDocumentedDomesticContent(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Documented Domestic-Content Modules'; } },
  InventoryByWarehouse(language?: string) { language = language || getLanguage(); switch (language) { default: return 'By Warehouse'; } },
  InventoryByPo(language?: string) { language = language || getLanguage(); switch (language) { default: return 'By Purchase Order'; } },
  InventoryByStatus(language?: string) { language = language || getLanguage(); switch (language) { default: return 'By Status'; } },
  InventoryLogistics(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inbound Logistics'; } },
  InventoryInboundLoads(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inbound Loads'; } },
  InventoryInboundUnits(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inbound Units'; } },
  InventorySupplyVsDemand(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Supply vs Demand'; } },
  InventoryTotalSites(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Total Sites'; } },
  InventoryModulesProcured(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules Procured'; } },
  InventoryModulesAllocatedToSites(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules Allocated to Sites'; } },
  InventoryModulesRequiredInProgress(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Required by In Progress Sites'; } },
  InventorySurplusDeficit(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Surplus / (Deficit) vs Demand'; } },
  InventorySitesByAllocation(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sites Complete / Partial / Unallocated'; } },
  InventoryOutbound(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Outbound — Warehouse to Job Site'; } },
  InventorySitesWithPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sites with Pallets Allocated'; } },
  InventorySitesWithDelivery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sites with a Delivery Requested'; } },
  InventoryPalletsAllocatedNotShipped(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Allocated, Not Yet Shipped'; } },
  InventoryPalletsDelivered(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Delivered to Sites'; } },
  InventoryModulesDelivered(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules Delivered to Sites'; } },
  InventorySitesArrivingAfterMobilization(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sites Arriving After Mobilization'; } },
  InventoryQualityFlags(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Quality / Risk Flags'; } },
  InventoryDamagedUnits(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Damaged Units'; } },
  InventorySerialsPending(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets with Serials Pending'; } },
  InventoryPalletsOnInactiveSites(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets on Held or Canceled Sites'; } },
  InventoryProvisionalPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Provisional Pallet Numbers'; } },
  InventoryStockAllocatedVsAvailable(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Warehouse Stock — Allocated vs Available'; } },
  InventoryPalletsOnHand(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets On Hand'; } },
  InventoryPalletsAvailable(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Available'; } },
  InventoryModulesAvailable(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Modules Available'; } },
  InventoryWarehouseCost(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Warehousing Cost'; } },
  InventoryInvoicedToDate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Invoiced to Date'; } },
  InventoryUnpaidInvoices(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Unpaid Invoices'; } },
  InventoryPalletsExpected(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Expected'; } },
  InventoryPalletsLoaded(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Loaded'; } },
  InventoryPalletsOutstanding(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pallets Outstanding'; } },
  InventoryRoleView(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inventory (view)'; } },
  InventoryRoleEdit(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inventory (edit)'; } },
  InventoryRoleFinance(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inventory finance fields'; } },
  InventoryNothingSelected(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Select at least one pallet first'; } },
  InventorySelectSiteFirst(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Choose a site'; } },
  InventorySiteInactiveWarning(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This site is on hold or canceled. Assigning anyway will be recorded.'; } },
  InventoryNoFreePallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No unassigned pallets are in stock'; } },
  InventoryNothingToAllocate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This site has no work order size, so its requirement is unknown'; } },
  InventoryLotHasPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This lot still has pallets; remove them first'; } },
  InventoryWarehouseHasPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This warehouse still holds pallets; move them first'; } },
  InventorySiteHasPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This site still has pallets assigned; unassign them first'; } },
  InventoryDeliveryHasShipped(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This delivery has already shipped and cannot be changed'; } },
  InventoryDeliveryNotEmpty(language?: string) { language = language || getLanguage(); switch (language) { default: return 'A delivery needs at least one pallet'; } },
  InventoryStatusNotAllowed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'That status is set by the delivery workflow, not by hand'; } },
  InventoryImportNoFile(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Choose a workbook to import'; } },
  InventoryImportSheetMissing(language?: string) { language = language || getLanguage(); switch (language) { default: return 'The workbook is missing a sheet this import needs'; } },
  InventoryImportDryRunNote(language?: string) { language = language || getLanguage(); switch (language) { default: return 'A dry run reads the workbook and reports what would change, without writing anything.'; } },
  InventoryTaxLiabilityNote(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Use tax is estimated as quantity x unit cost x use tax rate, less the sales tax already paid. Confirm the rule with finance before relying on it.'; } },
  InventoryRequirementBasisNote(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Requirements follow the funded work order size. The current design is shown beside it where the two differ.'; } },
  InventoryNoPallets(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No pallets'; } },
  InventoryNoSites(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No sites'; } },
  InventoryNoDeliveries(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No deliveries'; } },
  InventoryNoShipments(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No shipments'; } },
  InventoryNoSerials(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No serial records'; } },
  InventoryNoReductions(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No reductions posted'; } },
  InventoryNoInvoices(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No warehouse invoices'; } },
  InventoryNoEvents(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No activity recorded'; } },
  InventoryEquipmentHeading(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Equipment & Inventory'; } },
  InventoryPalletNoLongerAvailable(palletNumber: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Pallet ${palletNumber} is no longer available to assign`; } },
  InventoryPalletNumberTaken(palletNumber: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Pallet number ${palletNumber} is already in use`; } },
  InventoryAssignedPallets(pallets: number, site: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `${pallets} pallet(s) assigned to ${site}`; } },
  InventoryUnassignedPallets(pallets: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `${pallets} pallet(s) unassigned`; } },
  InventorySelectedCount(selected: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `${selected} selected`; } },
  InventoryShowingCount(shown: number, total: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `${shown} of ${total} pallets`; } },
  InventoryShortfall(modules: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `${modules} module(s) still short`; } },
  InventoryDeliveryAdvanced(status: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Delivery is now ${status}`; } },
  InventoryPalletsOnDelivery(pallets: number, modules: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `${pallets} pallet(s) · ${modules} module(s)`; } },
  InventoryDaysEarly(days: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `${days} day(s) early`; } },
  InventoryDaysLate(days: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `${days} day(s) late`; } },
  InventoryPreviousPage(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Previous'; } },
  InventoryNextPage(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Next'; } },
  InventoryAllSites(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Sites'; } },
  InventorySelectOrgFirst(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Select an organization before importing'; } },
  InventoryImportedFrom(file: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Imported from ${file}`; } },
  InventoryImportUnknownWarehouse(name: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Unknown warehouse "${name}"`; } },
  InventoryImportUnknownSite(siteId: string, palletNumber: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Pallet ${palletNumber} is assigned to site ${siteId}, which is not in the workbook`; } },
  InventoryImportUnknownPallet(palletNumber: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Receipt references pallet ${palletNumber}, which is not in the register`; } },
  InventoryImportNoLot(palletNumber: string, language?: string) { language = language || getLanguage(); switch (language) { default: return `Pallet ${palletNumber} has no matching purchase order or contract`; } },
  InventorySerialsOf(onFile: number, expected: number, language?: string) { language = language || getLanguage(); switch (language) { default: return `${onFile} of ${expected}`; } },
  // QA test results (/test-results)
  TestResults(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Test Results'; } },
  TestResultsSubtitle(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Runs and reports posted by QA'; } },
  ApiKeys(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Api Keys'; } },
  Show(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Show'; } },
  TestResultsKeysHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'One key per endpoint, for this environment. QA sends it as an Authorization: Bearer header, or as x-api-key. It belongs in their environment, never in a repository.';
    }
  },
  KeyNotConfigured(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'No key is configured in this environment — posts to it are refused.';
    }
  },
  AutomatedTests(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Automated tests'; } },
  ManualTests(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Manual tests'; } },
  LatestRun(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Latest run'; } },
  PassRate(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pass rate'; } },
  Passed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Passed'; } },
  Failed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Failed'; } },
  Duration(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Duration'; } },
  Target(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Target'; } },
  Commit(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Commit'; } },
  Report(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Report'; } },
  Runs(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Runs'; } },
  Run(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Run'; } },
  TestsRun(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tests run'; } },
  Last7Days(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Last 7 days'; } },
  Last30Days(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Last 30 days'; } },
  SevenDayAverage(language?: string) { language = language || getLanguage(); switch (language) { default: return '7-day average'; } },
  VsPriorWeek(language?: string) { language = language || getLanguage(); switch (language) { default: return 'vs prior week'; } },
  Points(language?: string) { language = language || getLanguage(); switch (language) { default: return 'pts'; } },
  FailingNow(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Failing now'; } },
  FailuresNew(language?: string) { language = language || getLanguage(); switch (language) { default: return 'new'; } },
  NewFailures(language?: string) { language = language || getLanguage(); switch (language) { default: return 'New failures'; } },
  FailuresPersisting(language?: string) { language = language || getLanguage(); switch (language) { default: return 'persisting'; } },
  FailuresResolved(language?: string) { language = language || getLanguage(); switch (language) { default: return 'resolved'; } },
  GreenStreak(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Green streak'; } },
  ConsecutivePassingRuns(language?: string) { language = language || getLanguage(); switch (language) { default: return 'consecutive passing runs'; } },
  MostFrequentFailures(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Most frequent failures, last 30 days'; } },
  NoTestRunsYet(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No runs have been reported yet'; } },
  NoReportAttached(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This run has no report attached'; } },
  OpenReport(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Open report'; } },
  Tests(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tests'; } },
  DeleteTestRunConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delete this run and its report? This cannot be undone.'; } },
  TestRunDeleted(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Run deleted'; } },
  Started(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Started'; } },
  Previous(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Previous'; } },
  Next(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Next'; } },
  ShowPassedTests(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Show passed tests'; } },
  HidePassedTests(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Hide passed tests'; } },
  Host(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Host'; } },
  Platform(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Platform'; } },
  Received(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Received'; } },
  ResidentialTrackerImport(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Residential Tracker Import'; } },
  ResidentialTrackerImportHelp(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Upload the Residential Pipeline Tracker <b>.xlsx</b> to seed Residential Prepaid projects: the project spine, the dealer, 37 parameters per row from the <b>Residential Pipeline Tracker</b> and <b>Payment Milestones</b> sheets, and each milestone\'s review status and approval date. Idempotent: keyed by <b>ClickUp ID</b>, so re-uploading updates rather than duplicates — it refreshes every estimate, but leaves a parameter that tracks no actual alone once somebody has filled it in. Refuses the file outright, before writing anything, if the tracker\'s columns have moved.';
    }
  },
  Dealer(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Dealer'; } },
  ResidentialTrackerDealerHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The partner whose pipeline this file is. Every project in it takes the dealer as its EPC, which is what lets the dealer\'s own people pick up their tasks. Leave it blank to keep whatever each project already has.';
    }
  },
  UploadResidentialTracker(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Upload Residential tracker .xlsx'; } },
  Importing(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Importing…'; } },
  ImportingTracker(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Importing… a full tracker takes a few minutes.'; } },
  Wipe(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wipe'; } },
  Wiping(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wiping…'; } },
  WipeResidentialProjects(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wipe all Residential projects'; } },
  WipeResidentialProjectsConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wipe all Residential projects?'; } },
  WipeResidentialProjectsBody(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This permanently deletes every Residential Prepaid project in your org and all of its child records — milestones, tasks, documents & versions, funding sources, cash events, parameters, stakeholders, contacts, links and events. RIC and every other vertical are untouched. Re-import the tracker to rebuild.';
    }
  },
  WipingResidentialProjects(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wiping residential projects…'; } },
  ResidentialTrackerImported(rows: number, created: number, updated: number, skipped: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Imported ${rows} rows: ${created} created, ${updated} updated, ${skipped} skipped`;
    }
  },
  IncorrectPassword(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Incorrect password'; } },
  ConfirmWithPassword(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Your password'; } },
  ConfirmWithPasswordHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This cannot be undone, so it takes your own password as well as your confirmation.';
    }
  },
  RicTrackerImport(language?: string) { language = language || getLanguage(); switch (language) { default: return 'RIC Tracker Import'; } },
  RicTrackerImportHelp(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Upload the RIC Portfolio Tracker <b>.xlsx</b> to seed Project Management with RIC (V1) projects, milestone dates, funding sources, the WO#1–3 / M0–M6 cash ledger, and key parameters. Idempotent — re-uploading updates existing projects (keyed by Site ID / RIC #). Use <b>Wipe all RIC projects</b> first for a clean rebuild.';
    }
  },
  UploadRicTracker(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Upload RIC tracker .xlsx'; } },
  ImportingRicTracker(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Importing… this can take a few minutes for a large tracker.'; } },
  WipeRicProjects(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wipe all RIC projects'; } },
  WipeRicProjectsConfirm(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wipe all RIC projects?'; } },
  WipeRicProjectsBody(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'This permanently deletes every RIC project in your org and all of its child records — milestones, tasks, documents & versions, funding sources, cash events, parameters, stakeholders, change/purchase orders, contacts, links, and events. Importer-created portfolios and owner/EPC/utility registry rows are removed once nothing else references them; utility accounts are UNLINKED (not deleted). Re-import the tracker to rebuild.';
    }
  },
  WipingRicProjects(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Wiping RIC projects…'; } },
  RicProjectsWiped(projects: number, childRecords: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Wiped ${projects} RIC project(s) and ${childRecords} child records`;
    }
  },
  RicTrackerImported(rows: number, created: number, updated: number, skipped: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Imported ${rows} rows: ${created} created, ${updated} updated, ${skipped} skipped (no WO #1)`;
    }
  },
  SgipIncentive(language?: string) { language = language || getLanguage(); switch (language) { default: return 'SGIP Incentive'; } },
  FundModelImport(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Fund Model Import'; } },
  FundModelImportHelp(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Upload the <b>Prepaid PPA Fund Model .xlsx</b> — the fund-level workbook behind the SGIP residential projects. It prices the whole portfolio, one row per project, so this is an import rather than a financial model upload: every project it names is found here by its <b>ClickUp ID</b> and takes the contract fields and the Fund\'s economics as parameters, plus the Fund\'s day-one <b>Sources</b> and <b>Uses</b>, which then show on the project\'s Budget tab. Idempotent: re-uploading refreshes every figure the model owns, and leaves alone any value somebody has typed in by hand. Refuses the file outright, before writing anything, if the workbook\'s columns have moved.';
    }
  },
  UploadFundModel(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Upload Prepaid PPA Fund Model .xlsx'; } },
  UnrecognizedFundModel(language?: string) { language = language || getLanguage(); switch (language) { default: return 'That file is not a Prepaid PPA Fund Model workbook.'; } },
  FundModelImported(matched: number, rows: number, parameters: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Imported ${matched} of ${rows} priced projects: ${parameters} values written`;
    }
  },
  // Residential's own, and deliberately not CashSources/CashUses even though they read the same today:
  // those label the banner tile every vertical shares, and a change to residential's wording must not be
  // able to reach RIC's.
  FundSources(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sources'; } },
  FundUses(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Uses'; } },
  FundSourcesUsesHint(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The Fund\'s own cash on day one, as the Prepaid PPA Fund Model computes it. The two sides are the same money and should total the same.';
    }
  },
  ResidentialProjectsWiped(projects: number, childRecords: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Wiped ${projects} residential project(s) and ${childRecords} child records`;
    }
  },
  // --- Document Management ---------------------------------------------------------------------------
  DocumentManagement(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Document Management'; } },
  AllDocuments(language?: string) { language = language || getLanguage(); switch (language) { default: return 'All Documents'; } },
  NewFolder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'New Folder'; } },
  Folder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Folder'; } },
  Folders(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Folders'; } },
  Trash(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Trash'; } },
  Starred(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Starred'; } },
  MoveTo(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Move to'; } },
  Permissions(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Permissions'; } },
  Everyone(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Everyone'; } },
  RestoreItem(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Restore'; } },
  DeleteForever(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Delete forever'; } },
  EmptyFolder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This folder is empty'; } },
  TrashIsEmpty(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Nothing in the trash'; } },
  AccessViewer(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Can view'; } },
  AccessEditor(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Can edit'; } },
  AccessManager(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Can manage'; } },
  PermissionsInherited(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inherited from the folder above'; } },
  PermissionsOwn(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Set here'; } },
  PermissionsInheritAgain(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Inherit from the folder above'; } },
  PermissionsRestamped(folders: number, files: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Permissions updated on ${folders} folder(s) and ${files} file(s)`;
    }
  },
  DriveNameTaken(name: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Something here is already called "${name}"`;
    }
  },
  DriveCannotMoveIntoItself(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'A folder cannot be moved into itself or into one of its own subfolders';
    }
  },
  DriveDeleteNeedsTrash(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Move it to the trash first — deleting for good only happens from there';
    }
  },
  DriveRevertedTo(versionNumber: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Reverted to version ${versionNumber}`;
    }
  },
  DriveBadPermission(principal: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Not a permission this can grant: ${principal}`;
    }
  },
  DriveDuplicateElsewhere(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'The same file is already filed elsewhere';
    }
  },
  DriveManagedElsewhere(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This document is filed from Project Management — remove it there'; } },
  DriveUploadGone(language?: string) { language = language || getLanguage(); switch (language) { default: return 'That upload has already been filed — choose the file again'; } },
  ViewAsList(language?: string) { language = language || getLanguage(); switch (language) { default: return 'List'; } },
  ViewAsGrid(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Grid'; } },
  LoadMore(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Show more'; } },
  ShowingOf(shown: number, total: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Showing ${shown} of ${total}`;
    }
  },
  DriveNoPreview(language?: string) { language = language || getLanguage(); switch (language) { default: return 'There is nothing to show for this kind of file — download it instead'; } },
  ViewGallery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'View photos'; } },
  Gallery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Photos'; } },
  GalleryPreparing(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Looking through the archive…'; } },
  GalleryFailed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'The archive could not be read — download it instead'; } },
  GalleryUnsupported(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Only .zip archives can be shown as photos'; } },
  GalleryEmpty(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No pictures in this archive'; } },
  GalleryNoDiskSpace(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Not enough free space to read the archive'; } },
  DownloadArchive(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Download the archive'; } },
  ReindexGallery(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Rebuild photo previews'; } },
  GalleryOtherFiles(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return count === 1 ? 'and 1 file that is not a picture' : `and ${count} files that are not pictures`;
    }
  },
  GalleryOf(shown: number, total: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return total === 1 ? '1 photo' : `${shown} of ${total} photos`;
    }
  },
  DriveTextTooLong(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Too long to save from here; edit it outside and upload a new version'; } },
  DriveTextTruncated(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Showing the beginning of a long file, so it cannot be edited here'; } },
  DriveSaveMakesVersion(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Saving creates a new version'; } },
  DriveViewAsPdf(language?: string) { language = language || getLanguage(); switch (language) { default: return 'View as PDF'; } },
  DriveConverting(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Being converted for viewing…'; } },
  DownloadFolder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Download as zip'; } },
  Sheet(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Sheet'; } },
  DrivePreviewTruncated(rows: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Showing the first ${rows} rows`;
    }
  },
  DriveZipReady(files: number, skipped: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return skipped ? `${files} file(s) zipped, ${skipped} left out` : `${files} file(s) zipped`;
    }
  },
  Share(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Share'; } },
  ShareLink(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Share link'; } },
  ShareLinks(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Share links'; } },
  CreateShareLink(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Create a link'; } },
  LinkCopied(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Link copied'; } },
  Revoke(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Turn off'; } },
  Visits(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Visits'; } },
  NeverExpires(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No expiry'; } },
  ShareExpiry(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Expires after'; } },
  ShareWithCode(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Ask for an access code'; } },
  ShareWithCodeHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Send the code separately — anyone holding only the link is turned away'; } },
  ShareRequireEmail(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Ask visitors for their email'; } },
  ShareRequireEmailHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Recorded against each visit. Nothing checks it, so read it as a sign-in sheet'; } },
  ShareCodeOnce(language?: string) { language = language || getLanguage(); switch (language) { default: return 'copy it now — only a hash is kept, so nothing can show it again'; } },
  ShareLabel(language?: string) { language = language || getLanguage(); switch (language) { default: return 'What is this link for?'; } },
  NoShareLinks(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No links yet'; } },
  ShareStateLive(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Live'; } },
  ShareStateRevoked(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Turned off'; } },
  ShareStateExpired(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Expired'; } },
  ShareStateLocked(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Locked'; } },
  ShareAccessCode(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Access code'; } },
  ShareEnterCode(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This link needs an access code'; } },
  ShareWrongCode(language?: string) { language = language || getLanguage(); switch (language) { default: return 'That code did not work'; } },
  ShareEnterEmail(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Tell us who you are before opening this'; } },
  ShareLinkNotValid(language?: string) { language = language || getLanguage(); switch (language) { default: return 'This link is not valid'; } },
  ShareLinkUnavailable(reason: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: switch (reason) {
        case 'revoked': return 'This link has been turned off';
        case 'expired': return 'This link has expired';
        case 'locked': return 'Too many wrong codes — try again in a few minutes';
        default: return 'This document is no longer available';
      }
    }
  },
  SharedWithYou(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Shared with you'; } },
  SearchResults(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Search results'; } },
  NoSearchResults(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Nothing matched'; } },
  SearchTruncated(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Showing the first matches — narrow the search to see fewer'; } },
  ReadWithOcr(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Read this with OCR'; } },
  ReadWithOcrHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Scans and photos are not read automatically. This queues one.'; } },
  TextPending(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Being read for search\u2026'; } },
  TextFailed(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Could not be read for search'; } },
  DocumentsRoleView(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Open the Documents module (per-folder permissions then apply)'; } },
  DocumentsRoleAdmin(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Administer the drive: root folders, any folder’s permissions, the whole tree'; } },
  Options(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Options'; } },
  MoveToTrash(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Move to trash'; } },
  Size(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Size'; } },
  Versions(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Versions'; } },
  Access(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Access'; } },
  By(language?: string) { language = language || getLanguage(); switch (language) { default: return 'By'; } },
  NoActivity(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Nothing has happened to this yet'; } },
  NoPermissionsSet(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Nobody has been given access here'; } },
  AddPermission(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Add someone…'; } },
  NoFolders(language?: string) { language = language || getLanguage(); switch (language) { default: return 'No folders yet'; } },
  DriveSearchHint(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Search this folder'; } },
  DriveSearchAll(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Search everything you can see'; } },
  OpenFolder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Open'; } },
  MoveToFolder(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Move to folder'; } },
  MoveToRoot(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Top level'; } },
  DriveUploadHere(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Drop files here, or click to choose'; } },
  DriveNoFolderSelected(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Pick a folder on the left to see what is in it'; } },
  DriveNothingStarred(language?: string) { language = language || getLanguage(); switch (language) { default: return 'Nothing starred yet'; } },
  DriveDeleteFolderConfirm(name: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Delete "${name}" and everything inside it for good? This cannot be undone.`;
    }
  },
  DriveDeleteFileConfirm(name: string, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return `Delete "${name}" and all of its versions for good? This cannot be undone.`;
    }
  },
  DriveVersionsCount(count: number, language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return count === 1 ? '1 version' : `${count} versions`;
    }
  },
  DocumentsNotPermitted(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'You do not have access to Document Management';
    }
  },
};

(strings as any).month0 = strings.January;
(strings as any).month1 = strings.February;
(strings as any).month2 = strings.March;
(strings as any).month3 = strings.April;
(strings as any).month4 = strings.May;
(strings as any).month5 = strings.June;
(strings as any).month6 = strings.July;
(strings as any).month7 = strings.August;
(strings as any).month8 = strings.September;
(strings as any).month9 = strings.October;
(strings as any).month10 = strings.November;
(strings as any).month11 = strings.December;
(strings as any).day0 = strings.Sunday;
(strings as any).day1 = strings.Monday;
(strings as any).day2 = strings.Tuesday;
(strings as any).day3 = strings.Wednesday;
(strings as any).day4 = strings.Thursday;
(strings as any).day5 = strings.Friday;
(strings as any).day6 = strings.Saturday;

export const validationStrings = {
  required(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Required';
    }
  },
  tooLong(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Too long';
    }
  },
  mustBeUnique(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Must be unique';
    }
  },
  mustBeNumber(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Must be a number';
    }
  },
  lessThanMin(language?: string) {
    language = language || getLanguage();
    switch (language) {
      default: return 'Value is too low';
    }
  },
};
